import React, { useState, useEffect, useCallback } from "react";
import CustomerCenter from "./CustomerCenter";
import FinanceScreen from "./FinanceScreen";
import DashboardScreen from "./DashboardScreen";

const C = {
  bg:"#06080F",surface:"#0e1117",card:"#141820",border:"#1e2533",
  accent:"#F59E0B",accentDim:"#F59E0B22",text:"#F1F5F9",muted:"#64748B",
  danger:"#EF4444",success:"#22C55E",info:"#3B82F6",purple:"#8B5CF6",
};

/* ── Responsive styles (CSS media queries) ────────────────────────────────── */
const RESPONSIVE_STYLES = `
  * { box-sizing: border-box; }
  body { margin: 0; overflow-x: hidden; background: ${C.bg}; }

  /* ── Sidebar → bottom nav ─────────────────────────────────────────────── */
  .console-sidebar { width: 220px; flex-shrink: 0; background: ${C.surface}; border-right: 1px solid ${C.border}; display: flex; flex-direction: column; position: fixed; top: 0; bottom: 0; left: 0; z-index: 20; }
  .console-main   { margin-left: 220px; flex: 1; display: flex; flex-direction: column; min-height: 100vh; }
  .console-topbar { height: 52px; border-bottom: 1px solid ${C.border}; display: flex; align-items: center; padding: 0 28px; background: ${C.surface}; position: sticky; top: 0; z-index: 10; }
  .console-body   { flex: 1; padding: 28px; }
  .sidebar-brand  { padding: 18px 20px 14px; border-bottom: 1px solid ${C.border}; }
  .sidebar-nav    { flex: 1; padding: 10px 8px; overflow-y: auto; }
  .sidebar-footer { padding: 12px 16px; border-top: 1px solid ${C.border}; }
  .sidebar-label  { display: inline; }
  .bottom-nav     { display: none; }

  /* ── Tables: mobile card view ─────────────────────────────────────────── */
  .rc-table       { display: table; width: 100%; border-collapse: collapse; background: ${C.card}; }
  .rc-thead       { display: table-header-group; }
  .rc-tr          { display: table-row; }
  .rc-th, .rc-td  { display: table-cell; }
  .rc-card-label  { display: none; }
  .rc-actions-row { display: none; }

  /* ── Stat cards ───────────────────────────────────────────────────────── */
  .stat-grid { display: flex; flex-wrap: wrap; gap: 16px; margin-bottom: 32px; }

  /* ── Search bar ───────────────────────────────────────────────────────── */
  .rc-search { width: 280px; }

  /* ── Filter tabs ──────────────────────────────────────────────────────── */
  .rc-filter-tabs { display: flex; gap: 4px; margin-bottom: 16px; flex-wrap: wrap; }

  /* ── Modal / Drawer ───────────────────────────────────────────────────── */
  .rc-modal-inner { width: 100%; max-width: 440px; max-height: 92vh; overflow-y: auto; scrollbar-width: none; }
  .rc-modal-inner::-webkit-scrollbar { display: none; }
  .rc-drawer-inner { width: 480px; max-width: 95vw; }

  /* ── Login card ───────────────────────────────────────────────────────── */
  .login-card { padding: 44px 40px; width: 420px; max-width: calc(100vw - 40px); }

  @media (max-width: 768px) {
    .console-sidebar { position: fixed; top: auto; bottom: 0; left: 0; right: 0; width: 100%; height: 60px; flex-direction: row; border-right: none; border-top: 1px solid ${C.border}; z-index: 50; }
    .sidebar-brand   { display: none; }
    .sidebar-footer  { display: none; }
    .sidebar-nav     { flex: 1; padding: 0; display: flex; flex-direction: row; align-items: center; justify-content: space-around; overflow-x: auto; overflow-y: hidden; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
    .sidebar-nav::-webkit-scrollbar { display: none; }
    .sidebar-nav button { flex-direction: column; gap: 2px !important; padding: 6px 8px !important; font-size: 10px !important; min-width: 0; border-radius: 0 !important; margin: 0 !important; }
    .sidebar-label   { display: block; font-size: 9px !important; white-space: nowrap; }
    .console-main    { margin-left: 0; padding-bottom: 64px; min-height: 100dvh; }
    .console-topbar  { padding: 0 16px; height: 48px; }
    .console-body    { padding: 16px; }
    .bottom-nav      { display: flex; }

    /* Tables → card view */
    .rc-table-wrap   { border: none; border-radius: 0; }
    .rc-table        { display: block; }
    .rc-thead        { display: none; }
    .rc-tbody        { display: flex; flex-direction: column; gap: 10px; }
    .rc-tr           { display: flex; flex-direction: column; background: ${C.card}; border: 1px solid ${C.border}; border-radius: 10px; padding: 14px 16px; gap: 6px; cursor: pointer; }
    .rc-th, .rc-td   { display: block; padding: 0 !important; border: none !important; font-size: 13px; }
    .rc-card-label   { display: inline; font-size: 11px; font-weight: 700; color: ${C.muted}; text-transform: uppercase; letter-spacing: 0.5px; margin-right: 8px; }
    .rc-td-actions   { display: flex; gap: 8px; margin-top: 6px; flex-wrap: wrap; }
    .rc-actions-row  { display: block; }

    /* Stat cards */
    .stat-grid { gap: 10px; }
    .stat-grid > div { min-width: 0 !important; flex: 1 1 calc(50% - 5px) !important; }

    /* Search bar */
    .rc-search { width: 100%; }

    /* Filter tabs */
    .rc-filter-tabs { overflow-x: auto; flex-wrap: nowrap; -webkit-overflow-scrolling: touch; scrollbar-width: none; padding-bottom: 4px; }
    .rc-filter-tabs::-webkit-scrollbar { display: none; }

    /* Modal full screen on mobile */
    .rc-modal-inner { max-width: 100%; max-height: 100dvh; border-radius: 0; height: 100dvh; }
    .rc-drawer-inner { width: 100%; max-width: 100%; }

    /* Login */
    .login-card { padding: 32px 24px; margin: 0 16px; }
  }

  @media (max-width: 480px) {
    .stat-grid > div { flex: 1 1 100% !important; }
    .console-body { padding: 12px; }
    .rc-tr { padding: 12px 14px; }
  }

  @media (orientation: landscape) and (max-height: 500px) {
    .console-sidebar { height: 50px; }
    .sidebar-nav button { padding: 4px 6px !important; }
    .sidebar-label { font-size: 8px !important; }
    .console-main { padding-bottom: 54px; }
  }
`;

async function api(method, path, body) {
  const res = await fetch(`/api/admin${path}`, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (res.status === 401) { window.__adminLogout?.(); return; }
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

function fmt(d) { if(!d) return "—"; const x=new Date(d); return x.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}); }
function inr(n) { return `₹${n}`; }

function Btn({children,onClick,variant="primary",disabled,style,type="button"}) {
  const v={primary:{background:C.accent,color:"#000"},outline:{background:"transparent",color:C.accent,border:`1.5px solid ${C.accent}`},ghost:{background:"transparent",color:C.muted,border:`1.5px solid ${C.border}`},danger:{background:C.danger,color:"#fff"},success:{background:C.success,color:"#000"},sm:{background:C.accent,color:"#000",padding:"4px 10px",fontSize:12},smGhost:{background:"transparent",color:C.muted,border:`1px solid ${C.border}`,padding:"4px 10px",fontSize:12},smDanger:{background:C.danger,color:"#fff",padding:"4px 10px",fontSize:12},smSuccess:{background:C.success,color:"#000",padding:"4px 10px",fontSize:12}};
  return <button type={type} disabled={disabled} onClick={onClick} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:6,fontSize:13,fontWeight:600,cursor:disabled?"not-allowed":"pointer",border:"none",fontFamily:"inherit",opacity:disabled?0.5:1,...(v[variant]||v.primary),...style}}>{children}</button>;
}

function Badge({label,color="grey"}) {
  const m={green:{bg:"#22C55E22",text:"#22C55E"},red:{bg:"#EF444422",text:"#EF4444"},yellow:{bg:"#F59E0B22",text:"#F59E0B"},blue:{bg:"#3B82F622",text:"#3B82F6"},purple:{bg:"#8B5CF622",text:"#8B5CF6"},grey:{bg:"#64748B22",text:"#64748B"}};
  const{bg,text}=m[color]||m.grey;
  return <span style={{background:bg,color:text,padding:"2px 8px",borderRadius:99,fontSize:11,fontWeight:600,whiteSpace:"nowrap"}}>{label}</span>;
}

function Spinner() {
  return <div style={{display:"flex",justifyContent:"center",padding:40}}><div style={{width:28,height:28,borderRadius:"50%",border:`3px solid ${C.border}`,borderTopColor:C.accent,animation:"spin 0.7s linear infinite"}}/><style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style></div>;
}

function StatCard({label,value,sub,color}) {
  return <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px 20px",minWidth:0}}><div style={{fontSize:11,color:C.muted,marginBottom:6,textTransform:"uppercase",letterSpacing:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{label}</div><div style={{fontSize:24,fontWeight:700,color:color||C.text}}>{value??"—"}</div>{sub&&<div style={{fontSize:11,color:C.muted,marginTop:4,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{sub}</div>}</div>;
}

function Pagination({page,total,limit,onPage}) {
  const tot=Math.ceil(total/limit); if(tot<=1) return null;
  return <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",color:C.muted,fontSize:13}}><Btn variant="ghost" disabled={page<=1} onClick={()=>onPage(page-1)} style={{padding:"5px 12px",fontSize:12}}>← Prev</Btn><span>Page {page} of {tot} ({total} total)</span><Btn variant="ghost" disabled={page>=tot} onClick={()=>onPage(page+1)} style={{padding:"5px 12px",fontSize:12}}>Next →</Btn></div>;
}

function EmptyState({message}) { return <div style={{padding:"40px 0",textAlign:"center",color:C.muted,fontSize:14}}>{message}</div>; }

function Th({children,style}) { return <th className="rc-th" style={{padding:"10px 14px",textAlign:"left",fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.8,borderBottom:`1px solid ${C.border}`,background:C.surface,...style}}>{children}</th>; }
function Td({children,style,label}) { return <td className="rc-td" style={{padding:"10px 14px",fontSize:13,color:C.text,borderBottom:`1px solid ${C.border}`,verticalAlign:"middle",...style}}>{label&&<span className="rc-card-label">{label}</span>}{children}</td>; }
function TableWrap({children}) { return <div className="rc-table-wrap" style={{overflowX:"auto",borderRadius:10,border:`1px solid ${C.border}`}}>{children}</div>; }

function FilterTabs({tabs,active,onChange}) {
  return <div className="rc-filter-tabs">{tabs.map(t=><button key={t.value} onClick={()=>onChange(t.value)} style={{padding:"6px 14px",borderRadius:6,fontSize:13,fontWeight:600,border:"none",cursor:"pointer",fontFamily:"inherit",background:active===t.value?C.accent:C.card,color:active===t.value?"#000":C.muted,whiteSpace:"nowrap",flexShrink:0}}>{t.label}</button>)}</div>;
}

function SearchBar({value,onChange,placeholder="Search…"}) {
  return <input className="rc-search" value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={{padding:"8px 12px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:7,color:C.text,fontSize:13,fontFamily:"inherit",outline:"none",marginBottom:16,boxSizing:"border-box"}}/>;
}

function Modal({title,onClose,children}) {
  return <div style={{position:"fixed",inset:0,background:"#000a",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center",padding:0}} onClick={onClose}><div className="rc-modal-inner" style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:28}} onClick={e=>e.stopPropagation()}><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}><span style={{fontWeight:700,fontSize:16}}>{title}</span><button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:22,cursor:"pointer",padding:8,minWidth:44,minHeight:44,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button></div>{children}</div></div>;
}

function Drawer({title,onClose,children}) {
  return <div style={{position:"fixed",inset:0,background:"#000a",zIndex:100,display:"flex",justifyContent:"flex-end"}} onClick={onClose}><div className="rc-drawer-inner" style={{background:C.surface,borderLeft:`1px solid ${C.border}`,overflowY:"auto",padding:24}} onClick={e=>e.stopPropagation()}><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}><span style={{fontWeight:700,fontSize:16}}>{title}</span><button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:22,cursor:"pointer",padding:8,minWidth:44,minHeight:44,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button></div>{children}</div></div>;
}

// ── Login ──────────────────────────────────────────────────────────────────────
function LoginScreen({onLogin}) {
  const [email,setEmail]=useState(""); const [password,setPassword]=useState("");
  const [error,setError]=useState(""); const [loading,setLoading]=useState(false);
  async function submit(e) {
    e.preventDefault(); setError(""); setLoading(true);
    try { const d=await api("POST","/auth/login",{email,password}); onLogin(d.admin); }
    catch(err){ setError(err.message); } finally { setLoading(false); }
  }
  const inp={width:"100%",padding:"10px 12px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:7,color:C.text,fontSize:14,fontFamily:"inherit",boxSizing:"border-box",outline:"none"};
  const lbl={display:"block",fontSize:11,color:C.muted,marginBottom:5,fontWeight:700,letterSpacing:0.8};
  return (
    <div style={{minHeight:"100dvh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Segoe UI',system-ui,sans-serif",padding:"20px 0"}}>
      <div className="login-card" style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,boxSizing:"border-box"}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <img src="/rik-logo.png" alt="Rik.ai" style={{width:64,height:64,objectFit:"contain",marginBottom:8}}/>
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
            <span style={{fontSize:22,fontWeight:700,color:C.text}}>Rik.ai</span>
            <span style={{background:C.accent,color:"#000",fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:4,letterSpacing:1.5}}>CONSOLE</span>
          </div>
          <div style={{fontSize:13,color:C.muted,marginTop:6}}>Internal admin access only</div>
        </div>
        <form onSubmit={submit}>
          <div style={{marginBottom:16}}><label style={lbl}>EMAIL</label><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="admin@rikai.tech" required autoFocus style={inp}/></div>
          <div style={{marginBottom:24}}><label style={lbl}>PASSWORD</label><input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" required style={inp}/></div>
          {error&&<div style={{background:"#EF444422",border:`1px solid #EF444444`,borderRadius:7,padding:"10px 14px",color:C.danger,fontSize:13,marginBottom:16}}>{error}</div>}
          <Btn type="submit" disabled={loading} style={{width:"100%",justifyContent:"center",padding:11}}>{loading?"Signing in…":"Sign In"}</Btn>
        </form>
      </div>
    </div>
  );
}

// ── Shell ──────────────────────────────────────────────────────────────────────
const NAV=[{id:"dashboard",label:"Dashboard",icon:"📊"},{id:"participants",label:"Participants",icon:"👥"},{id:"customers",label:"Customers",icon:"🏢"},{id:"surveys",label:"Surveys",icon:"📋"},{id:"suppliers",label:"Suppliers",icon:"🔌"},{id:"payouts",label:"Payouts",icon:"💳"},{id:"referrals",label:"Referrals",icon:"🔗"},{id:"tickets",label:"Tickets",icon:"🎫"},{id:"finance",label:"Finance",icon:"📈"},{id:"corpus",label:"Corpus",icon:"🧠"}];

// ── Dashboard ──────────────────────────────────────────────────────────────────
// DashboardScreen imported from ./DashboardScreen.js

// ── Pool Explorer ─────────────────────────────────────────────────────────────
// Values MUST match what Registration.js and seed.js write to the DB exactly
const POOL_OPTS = {
  gender:        ["Male","Female","Non-binary","Prefer not to say"],
  ageBand:       ["18–24","25–34","35–44","45–54","55+","55–64","65+"],
  cityTier:      ["Tier 1","Tier 2","Tier 3","Rural"],
  language:      ["English","Hindi","Tamil","Telugu","Marathi","Bengali","Gujarati","Kannada","Malayalam","Punjabi"],
  education:     ["High School or below","Diploma / Vocational","Graduate (Bachelor's)","Post Graduate (Master's)","Doctorate / PhD","Other"],
  profession:    ["Student","Salaried — Private Sector","Salaried — Government / PSU","Business Owner / Self Employed","Freelancer / Consultant","Homemaker","Retired","Healthcare Professional","IT / Tech Professional","Finance / Banking","Other"],
  yearsWorking:  ["Less than 1 year","1–3 years","3–7 years","7–15 years","15+ years","Not applicable"],
  incomeBand:    ["Below ₹3 LPA","₹3–6 LPA","₹6–10 LPA","₹10–20 LPA","₹20–40 LPA","Above ₹40 LPA","Prefer not to say"],
  householdRole: ["Primary decision maker","Joint decision maker","Influencer (input but not final call)","Not involved in household decisions"],
  deviceUsage:   ["Smartphone only","Laptop/Desktop only","Both smartphone and laptop/desktop"],
  internetHours: ["Less than 1 hour","1–3 hours","3–6 hours","6–10 hours","10+ hours"],
  socialMedia:   ["Instagram","Facebook","WhatsApp","LinkedIn","YouTube","Twitter/X","Snapchat","Pinterest"],
  contentType:   ["News","Entertainment / OTT","Finance / Business","Travel / Lifestyle","Education / How-to","Technology","Health & Wellness","Sports"],
  primaryChannel:["Amazon","Flipkart","Myntra","D2C brand websites","Physical retail stores","Social commerce"],
  industries:    ["FMCG / Consumer Goods","Automobile","Financial Services","Healthcare / Pharma","Real Estate","EdTech / Education","Retail / E-commerce","Travel & Hospitality","Technology / SaaS","Media & Entertainment","Agriculture","Manufacturing","None in particular"],
  brandAwareness:["Very closely — I research before every purchase","Somewhat — I notice major launches","Occasionally","Rarely"],
};

// Canonical sort order for ordinal fields (overrides count-based sort in breakdown)
const BREAKDOWN_ORDER = {
  ageBand:      ["18–24","25–34","35–44","45–54","55+","55–64","65+"],
  cityTier:     ["Tier 1","Tier 2","Tier 3","Rural"],
  education:    ["High School or below","Diploma / Vocational","Graduate (Bachelor's)","Post Graduate (Master's)","Doctorate / PhD","Other"],
  yearsWorking: ["Less than 1 year","1–3 years","3–7 years","7–15 years","15+ years","Not applicable"],
  incomeBand:   ["Below ₹3 LPA","₹3–6 LPA","₹6–10 LPA","₹10–20 LPA","₹20–40 LPA","Above ₹40 LPA","Prefer not to say"],
  internetHours:["Less than 1 hour","1–3 hours","3–6 hours","6–10 hours","10+ hours"],
};

const POOL_FILTERS_EMPTY = {
  gender:[],ageBand:[],cityTier:[],language:[],
  education:[],profession:[],yearsWorking:[],incomeBand:[],householdRole:[],
  deviceUsage:[],internetHours:[],socialMedia:[],contentType:[],primaryChannel:[],
  industries:[],brandAwareness:[],
  verified:"",referralEligible:"",minSessions:"",maxSessions:"",
};

function PoolChipGroup({title,field,options,filters,onToggle}) {
  const INP={fontSize:11,color:C.muted,fontWeight:700,letterSpacing:0.8,marginBottom:7,textTransform:"uppercase"};
  return (
    <div style={{marginBottom:18}}>
      <div style={INP}>{title}</div>
      <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
        {options.map(opt=>{
          const sel=filters[field].includes(opt);
          return <button key={opt} onClick={()=>onToggle(field,opt)} style={{padding:"3px 9px",borderRadius:99,fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${sel?C.accent:C.border}`,background:sel?C.accentDim:"transparent",color:sel?C.accent:C.muted,fontFamily:"inherit",transition:"all 0.15s"}}>{opt}</button>;
        })}
      </div>
    </div>
  );
}

function BreakdownBar({title,data,orderKey}) {
  if(!data?.length) return null;
  const order=orderKey?BREAKDOWN_ORDER[orderKey]:null;
  const sorted=order
    ? [...data].sort((a,b)=>{const ai=order.indexOf(a.value),bi=order.indexOf(b.value);if(ai===-1&&bi===-1)return b.count-a.count;if(ai===-1)return 1;if(bi===-1)return -1;return ai-bi;})
    : data;
  const max=Math.max(...sorted.map(d=>d.count));
  const tot=sorted.reduce((s,d)=>s+d.count,0);
  return (
    <div style={{marginBottom:20,breakInside:"avoid"}}>
      <div style={{fontSize:11,color:C.muted,fontWeight:700,letterSpacing:0.8,marginBottom:8,textTransform:"uppercase"}}>{title}</div>
      {sorted.slice(0,8).map(item=>(
        <div key={item.value} style={{marginBottom:6}}>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:2}}>
            <span style={{color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"65%"}}>{item.value}</span>
            <span style={{color:C.muted,flexShrink:0}}>{item.count.toLocaleString()} ({tot>0?Math.round(item.count/tot*100):0}%)</span>
          </div>
          <div style={{height:4,background:C.border,borderRadius:2}}>
            <div style={{height:"100%",background:C.accent,borderRadius:2,width:`${max>0?Math.round(item.count/max*100):0}%`,transition:"width 0.4s ease"}}/>
          </div>
        </div>
      ))}
    </div>
  );
}

function PoolExplorer() {
  const [filters,setFilters]=useState(POOL_FILTERS_EMPTY);
  const [result,setResult]=useState(null);
  const [loading,setLoading]=useState(false);

  const activeCount=Object.entries(filters).reduce((n,[k,v])=>n+(Array.isArray(v)?v.length>0?1:0:v!==""?1:0),0);

  useEffect(()=>{
    const t=setTimeout(()=>{
      setLoading(true);
      const p=new URLSearchParams();
      Object.entries(filters).forEach(([k,v])=>{
        if(Array.isArray(v)){if(v.length)p.set(k,v.join(","));}
        else if(v!=="")p.set(k,v);
      });
      api("GET",`/participants/pool-analytics?${p.toString()}`)
        .then(d=>setResult(d)).catch(()=>{}).finally(()=>setLoading(false));
    },600);
    return()=>clearTimeout(t);
  },[filters]);

  function toggle(field,val){
    setFilters(f=>{const a=f[field]; return{...f,[field]:a.includes(val)?a.filter(x=>x!==val):[...a,val]};});
  }
  function clearAll(){setFilters(POOL_FILTERS_EMPTY);}

  const SECT={fontSize:12,fontWeight:700,letterSpacing:0.8,marginBottom:12,marginTop:4,paddingBottom:6,borderBottom:`1px solid ${C.border}`,textTransform:"uppercase"};
  const INP3={width:"100%",padding:"6px 8px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,fontSize:12,fontFamily:"inherit",boxSizing:"border-box"};
  const radio=(field,val,label)=>{const sel=filters[field]===val;return<button key={val} onClick={()=>setFilters(f=>({...f,[field]:val}))} style={{padding:"3px 10px",borderRadius:99,fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${sel?C.accent:C.border}`,background:sel?C.accentDim:"transparent",color:sel?C.accent:C.muted,fontFamily:"inherit"}}>{label}</button>;};

  return (
    <div style={{display:"flex",gap:16,alignItems:"flex-start"}}>

      {/* ── Filter Panel ────────────────────────────────────────────────────── */}
      <div style={{width:300,flexShrink:0,background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:18,overflowY:"auto",maxHeight:"calc(100vh - 130px)",scrollbarWidth:"none"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <span style={{fontWeight:700,fontSize:14,color:C.text}}>Filters</span>
          {activeCount>0&&<button onClick={clearAll} style={{background:"none",border:"none",color:C.accent,fontSize:12,cursor:"pointer",padding:0,fontFamily:"inherit",fontWeight:600}}>Clear all ({activeCount})</button>}
        </div>

        <div style={{...SECT,color:C.purple}}>Demographics</div>
        <PoolChipGroup title="Gender"    field="gender"    options={POOL_OPTS.gender}    filters={filters} onToggle={toggle}/>
        <PoolChipGroup title="Age Band"  field="ageBand"   options={POOL_OPTS.ageBand}   filters={filters} onToggle={toggle}/>
        <PoolChipGroup title="City Tier" field="cityTier"  options={POOL_OPTS.cityTier}  filters={filters} onToggle={toggle}/>
        <PoolChipGroup title="Language"  field="language"  options={POOL_OPTS.language}  filters={filters} onToggle={toggle}/>

        <div style={{...SECT,color:C.info,marginTop:8}}>Socio-Economic</div>
        <PoolChipGroup title="Education"     field="education"     options={POOL_OPTS.education}     filters={filters} onToggle={toggle}/>
        <PoolChipGroup title="Profession"    field="profession"    options={POOL_OPTS.profession}     filters={filters} onToggle={toggle}/>
        <PoolChipGroup title="Years Working" field="yearsWorking"  options={POOL_OPTS.yearsWorking}   filters={filters} onToggle={toggle}/>
        <PoolChipGroup title="Income Band"   field="incomeBand"    options={POOL_OPTS.incomeBand}     filters={filters} onToggle={toggle}/>
        <PoolChipGroup title="Household Role"field="householdRole" options={POOL_OPTS.householdRole}  filters={filters} onToggle={toggle}/>

        <div style={{...SECT,color:C.success,marginTop:8}}>Digital Behavior</div>
        <PoolChipGroup title="Device Usage"               field="deviceUsage"   options={POOL_OPTS.deviceUsage}   filters={filters} onToggle={toggle}/>
        <PoolChipGroup title="Internet Hours / Day"       field="internetHours" options={POOL_OPTS.internetHours} filters={filters} onToggle={toggle}/>
        <PoolChipGroup title="Social Media (any of)"      field="socialMedia"   options={POOL_OPTS.socialMedia}   filters={filters} onToggle={toggle}/>
        <PoolChipGroup title="Content Interests (any of)" field="contentType"   options={POOL_OPTS.contentType}   filters={filters} onToggle={toggle}/>
        <PoolChipGroup title="Shopping Channel (any of)"  field="primaryChannel"options={POOL_OPTS.primaryChannel}filters={filters} onToggle={toggle}/>

        <div style={{...SECT,color:C.accent,marginTop:8}}>Industry & Awareness</div>
        <PoolChipGroup title="Industries (any of)"   field="industries"    options={POOL_OPTS.industries}    filters={filters} onToggle={toggle}/>
        <PoolChipGroup title="Brand Awareness Level" field="brandAwareness"options={POOL_OPTS.brandAwareness}filters={filters} onToggle={toggle}/>

        <div style={{...SECT,color:C.muted,marginTop:8}}>Status & Activity</div>
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:700,letterSpacing:0.8,marginBottom:7,textTransform:"uppercase"}}>Verified</div>
          <div style={{display:"flex",gap:5}}>{radio("verified","","All")}{radio("verified","true","Yes")}{radio("verified","false","No")}</div>
        </div>
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:700,letterSpacing:0.8,marginBottom:7,textTransform:"uppercase"}}>Referral Eligible</div>
          <div style={{display:"flex",gap:5}}>{radio("referralEligible","","All")}{radio("referralEligible","true","Yes")}{radio("referralEligible","false","No")}</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:C.muted,fontWeight:700,letterSpacing:0.8,marginBottom:5,textTransform:"uppercase"}}>Min Sessions</div>
            <input type="number" min="0" value={filters.minSessions} onChange={e=>setFilters(f=>({...f,minSessions:e.target.value}))} style={INP3} placeholder="0"/>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:C.muted,fontWeight:700,letterSpacing:0.8,marginBottom:5,textTransform:"uppercase"}}>Max Sessions</div>
            <input type="number" min="0" value={filters.maxSessions} onChange={e=>setFilters(f=>({...f,maxSessions:e.target.value}))} style={INP3} placeholder="∞"/>
          </div>
        </div>
      </div>

      {/* ── Results Panel ───────────────────────────────────────────────────── */}
      <div style={{flex:1,minWidth:0}}>
        {/* Summary cards */}
        <div style={{display:"flex",gap:14,marginBottom:20,flexWrap:"wrap"}}>
          <div style={{background:C.card,border:`2px solid ${C.accent}33`,borderRadius:12,padding:"18px 24px",flex:"1 1 130px",minWidth:0}}>
            <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Matching Pool</div>
            <div style={{fontSize:40,fontWeight:800,color:loading?C.muted:C.accent,lineHeight:1}}>{loading?"…":(result?.count??0).toLocaleString()}</div>
            <div style={{fontSize:12,color:C.muted,marginTop:4}}>participants</div>
          </div>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"18px 24px",flex:"1 1 130px",minWidth:0}}>
            <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>% of Pool</div>
            <div style={{fontSize:40,fontWeight:800,color:loading?C.muted:C.text,lineHeight:1}}>{loading?"…":result&&result.total>0?Math.round(result.count/result.total*100):0}%</div>
            <div style={{fontSize:12,color:C.muted,marginTop:4}}>{(result?.total||0).toLocaleString()} total</div>
          </div>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"18px 24px",flex:"1 1 130px",minWidth:0}}>
            <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Active Filters</div>
            <div style={{fontSize:40,fontWeight:800,color:loading?C.muted:activeCount>0?C.purple:C.muted,lineHeight:1}}>{activeCount}</div>
            <div style={{fontSize:12,color:C.muted,marginTop:4}}>{activeCount===0?"no filters":"filters applied"}</div>
          </div>
        </div>

        {/* Breakdown grid */}
        {result&&!loading&&(
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"20px 24px",columns:"2 280px",columnGap:32}}>
            <BreakdownBar title="Gender"        data={result.breakdown?.byGender}/>
            <BreakdownBar title="Age Band"      data={result.breakdown?.byAgeBand}      orderKey="ageBand"/>
            <BreakdownBar title="City Tier"     data={result.breakdown?.byCityTier}     orderKey="cityTier"/>
            <BreakdownBar title="Income Band"   data={result.breakdown?.byIncomeBand}   orderKey="incomeBand"/>
            <BreakdownBar title="Education"     data={result.breakdown?.byEducation}    orderKey="education"/>
            <BreakdownBar title="Profession"    data={result.breakdown?.byProfession}/>
            <BreakdownBar title="Years Working" data={result.breakdown?.byYearsWorking} orderKey="yearsWorking"/>
            <BreakdownBar title="Device Usage"  data={result.breakdown?.byDeviceUsage}/>
            <BreakdownBar title="Language"      data={result.breakdown?.byLanguage}/>
            <BreakdownBar title="Top Cities"    data={result.breakdown?.topCities}/>
          </div>
        )}
        {loading&&<div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:60,textAlign:"center"}}><Spinner/></div>}
        {!result&&!loading&&<div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:60,textAlign:"center",color:C.muted,fontSize:14}}>Loading participant pool…</div>}
      </div>
    </div>
  );
}

// ── Participants ───────────────────────────────────────────────────────────────
function ParticipantsScreen() {
  const [view,setView]=useState("list");
  const [rows,setRows]=useState([]); const [total,setTotal]=useState(0);
  const [search,setSearch]=useState(""); const [verifiedFilter,setVerifiedFilter]=useState("all");
  const [page,setPage]=useState(1); const [loading,setLoading]=useState(true);
  const [selected,setSelected]=useState(null); const [detailLoading,setDetailLoading]=useState(false);
  const [actionMsg,setActionMsg]=useState("");
  const [tokenAmount,setTokenAmount]=useState(""); const [txnId,setTxnId]=useState("");
  const [verifying,setVerifying]=useState(false); const [verifyMsg,setVerifyMsg]=useState("");
  // UPI verification queue (distinct from account verification)
  const [upiRows,setUpiRows]=useState([]); const [upiTotal,setUpiTotal]=useState(0);
  const [upiCounts,setUpiCounts]=useState({}); const [upiStatus,setUpiStatus]=useState("unverified");
  const [upiSearch,setUpiSearch]=useState(""); const [upiPage,setUpiPage]=useState(1); const [upiLoading,setUpiLoading]=useState(false);

  const load=useCallback(()=>{
    setLoading(true);
    const vParam=verifiedFilter==="all"?"":verifiedFilter==="verified"?"true":"false";
    api("GET",`/participants?search=${encodeURIComponent(search)}&verified=${vParam}&page=${page}&limit=50`)
      .then(d=>{setRows(d.participants||[]);setTotal(d.total||0);}).catch(()=>{}).finally(()=>setLoading(false));
  },[search,verifiedFilter,page]);

  useEffect(()=>{const t=setTimeout(load,300); return()=>clearTimeout(t);},[load]);

  const loadUpi=useCallback(()=>{
    setUpiLoading(true);
    const sParam=upiStatus==="all"?"":upiStatus;
    api("GET",`/upi-verifications?status=${sParam}&search=${encodeURIComponent(upiSearch)}&page=${upiPage}&limit=50`)
      .then(d=>{setUpiRows(d.rows||[]);setUpiTotal(d.total||0);setUpiCounts(d.counts||{});}).catch(()=>{}).finally(()=>setUpiLoading(false));
  },[upiStatus,upiSearch,upiPage]);

  useEffect(()=>{ if(view!=="upi") return; const t=setTimeout(loadUpi,300); return()=>clearTimeout(t); },[view,loadUpi]);

  async function openDetail(p) {
    setDetailLoading(true); setSelected({...p,_loading:true});
    setTokenAmount(""); setTxnId(""); setVerifyMsg("");
    try { const d=await api("GET",`/participants/${p.id}`); setSelected(d.participant); }
    catch{ setSelected(p); } finally { setDetailLoading(false); }
  }

  async function doAction(action) {
    if(!selected) return;
    try { await api("PATCH",`/participants/${selected.id}/status`,{action}); setActionMsg("Done"); load(); setTimeout(()=>setActionMsg(""),2000); }
    catch(e){ setActionMsg(e.message); }
  }

  async function initiateVerification() {
    if(!selected) return;
    setVerifying(true); setVerifyMsg("");
    try {
      await api("POST",`/participants/${selected.id}/payment-details/initiate-verification`,
        { tokenAmount: parseFloat(tokenAmount), txnId: txnId.trim() });
      setVerifyMsg("Verification initiated — participant notified.");
      setTokenAmount(""); setTxnId("");
      const d = await api("GET",`/participants/${selected.id}`); setSelected(d.participant);
      if(view==="upi") loadUpi();
    } catch(e){ setVerifyMsg(e.message); }
    finally { setVerifying(false); }
  }

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:16,flexWrap:"wrap"}}>
        <FilterTabs tabs={[{value:"list",label:"👥 Participants List"},{value:"pool",label:"🔍 Pool Explorer"},{value:"upi",label:`💳 UPI Verification${upiCounts.unverified?` (${upiCounts.unverified})`:""}`}]} active={view} onChange={setView}/>
      </div>
      {view==="pool"&&<PoolExplorer/>}
      {view==="upi"&&<>
      <div style={{color:C.muted,fontSize:12,marginBottom:10}}>Participants who have added a UPI ID, by verification status. <b style={{color:C.text}}>UPI verification is separate from account verification.</b></div>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:4}}>
        <SearchBar value={upiSearch} onChange={v=>{setUpiSearch(v);setUpiPage(1);}} placeholder="Search name, email, mobile…"/>
      </div>
      <FilterTabs active={upiStatus} onChange={v=>{setUpiStatus(v);setUpiPage(1);}}
        tabs={[
          {value:"unverified",label:`Awaiting Admin${upiCounts.unverified?` (${upiCounts.unverified})`:""}`},
          {value:"pending_verification",label:`Awaiting Participant${upiCounts.pending_verification?` (${upiCounts.pending_verification})`:""}`},
          {value:"verification_failed",label:`Failed${upiCounts.verification_failed?` (${upiCounts.verification_failed})`:""}`},
          {value:"verified",label:`Verified${upiCounts.verified?` (${upiCounts.verified})`:""}`},
          {value:"all",label:"All"},
        ]}/>
      {upiLoading?<Spinner/>:(
        <>
          <TableWrap>
            <table className="rc-table"><thead className="rc-thead"><tr className="rc-tr"><Th>Name</Th><Th>Email</Th><Th>UPI ID</Th><Th>Status</Th><Th>Attempts</Th><Th>Initiated</Th></tr></thead>
            <tbody className="rc-tbody">
              {upiRows.length===0&&<tr className="rc-tr"><td colSpan={6}><EmptyState message="No UPI records in this status"/></td></tr>}
              {upiRows.map(r=>{
                const sm={unverified:{label:"Awaiting Admin",color:"grey"},pending_verification:{label:"Awaiting Participant",color:"yellow"},verification_failed:{label:"Failed",color:"red"},verified:{label:"Verified",color:"green"}}[r.upiVerificationStatus]||{label:r.upiVerificationStatus,color:"grey"};
                return (
                  <tr key={r.participantId} className="rc-tr" style={{cursor:"pointer"}} onClick={()=>openDetail({id:r.participantId,name:r.name})}>
                    <Td label="Name"><span style={{color:C.accent,fontWeight:600}}>{r.name}</span></Td>
                    <Td label="Email" style={{color:C.muted}}>{r.email}</Td>
                    <Td label="UPI ID">{r.maskedUpiId||"—"}</Td>
                    <Td label="Status"><Badge label={sm.label} color={sm.color}/></Td>
                    <Td label="Attempts" style={{color:C.muted}}>{r.verificationAttempts||0}/3</Td>
                    <Td label="Initiated" style={{color:C.muted}}>{r.verificationInitiatedAt?fmt(r.verificationInitiatedAt):"—"}</Td>
                  </tr>
                );
              })}
            </tbody></table>
          </TableWrap>
          <Pagination page={upiPage} total={upiTotal} limit={50} onPage={setUpiPage}/>
        </>
      )}
      </>}
      {view==="list"&&<>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:4}}>
        <SearchBar value={search} onChange={v=>{setSearch(v);setPage(1);}} placeholder="Search name, email, mobile…"/>
      </div>
      <FilterTabs tabs={[{value:"all",label:"All"},{value:"verified",label:"Verified"},{value:"unverified",label:"Unverified"}]} active={verifiedFilter} onChange={v=>{setVerifiedFilter(v);setPage(1);}}/>
      {loading?<Spinner/>:(
        <>
          <TableWrap>
            <table className="rc-table"><thead className="rc-thead"><tr className="rc-tr"><Th>Name</Th><Th>Email</Th><Th>Mobile</Th><Th>City</Th><Th>Age</Th><Th>Verified</Th><Th>Sessions</Th><Th>Joined</Th></tr></thead>
            <tbody className="rc-tbody">
              {rows.length===0&&<tr className="rc-tr"><td colSpan={8}><EmptyState message="No participants found"/></td></tr>}
              {rows.map(p=>(
                <tr key={p.id} className="rc-tr" style={{cursor:"pointer"}} onClick={()=>openDetail(p)}>
                  <Td label="Name"><span style={{color:C.accent,fontWeight:600}}>{p.name}</span></Td>
                  <Td label="Email" style={{color:C.muted}}>{p.email}</Td>
                  <Td label="Mobile" style={{color:C.muted}}>{p.mobile||"—"}</Td>
                  <Td label="City">{p.demographics?.city||"—"}</Td>
                  <Td label="Age">{p.demographics?.ageBand||"—"}</Td>
                  <Td label="Verified">{p.verified?<Badge label="Verified" color="green"/>:<Badge label="Unverified" color="grey"/>}</Td>
                  <Td label="Sessions">{p._count?.sessions||0}</Td>
                  <Td label="Joined" style={{color:C.muted}}>{fmt(p.createdAt)}</Td>
                </tr>
              ))}
            </tbody></table>
          </TableWrap>
          <Pagination page={page} total={total} limit={50} onPage={setPage}/>
        </>
      )}
      </>}
      {selected&&(
        <Drawer title={selected.name||"Participant"} onClose={()=>setSelected(null)}>
          {detailLoading&&selected._loading?<Spinner/>:(
            <div style={{fontSize:13}}>
              <div style={{marginBottom:16,display:"flex",gap:8,flexWrap:"wrap"}}>
                {!selected.verified&&<Btn variant="smSuccess" onClick={()=>doAction("verify")}>✓ Verify</Btn>}
                {selected.verified&&<Btn variant="smGhost" onClick={()=>doAction("unverify")}>Unverify</Btn>}
                {!selected.deletedAt&&<Btn variant="smDanger" onClick={()=>doAction("soft_delete")}>Soft Delete</Btn>}
                {selected.deletedAt&&<Btn variant="smGhost" onClick={()=>doAction("restore")}>Restore</Btn>}
                {actionMsg&&<span style={{color:C.success,alignSelf:"center"}}>{actionMsg}</span>}
              </div>
              <div style={{background:C.card,borderRadius:8,padding:14,marginBottom:12}}>
                <div style={{color:C.muted,fontSize:11,marginBottom:8,fontWeight:700,letterSpacing:0.8}}>ACCOUNT</div>
                <div><b>Email:</b> {selected.email}</div>
                <div><b>Mobile:</b> {selected.mobile||"—"}</div>
                <div><b>Verified:</b> {selected.verified?"Yes":"No"}</div>
                <div><b>Referral Eligible:</b> {selected.referralEligible?"Yes":"No"}</div>
                <div><b>Joined:</b> {fmt(selected.createdAt)}</div>
                {selected.deletedAt&&<div style={{color:C.danger}}><b>Deleted:</b> {fmt(selected.deletedAt)}</div>}
              </div>
              {/* Payout / UPI verification */}
              <div style={{background:C.card,borderRadius:8,padding:14,marginBottom:12}}>
                <div style={{color:C.muted,fontSize:11,marginBottom:8,fontWeight:700,letterSpacing:0.8}}>PAYOUT / UPI</div>
                {selected.paymentDetail?.hasUpi?(
                  <>
                    <div><b>UPI ID:</b> {selected.paymentDetail.maskedUpiId||"—"}</div>
                    <div style={{margin:"4px 0"}}><b>Status:</b>{" "}
                      {selected.paymentDetail.upiVerified
                        ? <Badge label="Verified" color="green"/>
                        : <Badge label={(selected.paymentDetail.upiVerificationStatus||"unverified").replace(/_/g," ")} color={selected.paymentDetail.upiVerificationStatus==="pending_verification"?"yellow":selected.paymentDetail.upiVerificationStatus==="verification_failed"?"red":"grey"}/>}
                    </div>
                    {selected.paymentDetail.verificationInitiatedAt&&<div style={{color:C.muted,fontSize:12}}>Initiated {fmt(selected.paymentDetail.verificationInitiatedAt)} · attempts: {selected.paymentDetail.verificationAttempts||0}/3</div>}
                    {!selected.paymentDetail.upiVerified&&(
                      <div style={{marginTop:10,borderTop:`1px solid ${C.border}`,paddingTop:10}}>
                        <div style={{color:C.muted,fontSize:11,marginBottom:6}}>
                          {selected.paymentDetail.upiVerificationStatus==="pending_verification"
                            ? "Re-send the penny-drop (overwrites the pending token)."
                            : "Send a small token (₹0.50–1.00) to this UPI, then record it here."}
                        </div>
                        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                          <input value={tokenAmount} onChange={e=>setTokenAmount(e.target.value)} type="number" step="0.01" min="0.01" max="5" placeholder="Amount (₹)"
                            style={{width:110,padding:"6px 9px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,fontSize:13,fontFamily:"inherit"}}/>
                          <input value={txnId} onChange={e=>setTxnId(e.target.value)} placeholder="UPI txn / UTR"
                            style={{flex:1,minWidth:140,padding:"6px 9px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,fontSize:13,fontFamily:"inherit"}}/>
                          <Btn variant="smSuccess" disabled={verifying||!tokenAmount||!txnId.trim()} onClick={initiateVerification}>
                            {verifying?"…":"Initiate Verification"}
                          </Btn>
                        </div>
                        {verifyMsg&&<div style={{marginTop:6,fontSize:12,color:verifyMsg.startsWith("Verification initiated")?C.success:C.danger}}>{verifyMsg}</div>}
                      </div>
                    )}
                  </>
                ):(
                  <div style={{color:C.muted}}>No UPI ID on file — participant must add one before payout.</div>
                )}
              </div>
              {selected.demographics&&(
                <div style={{background:C.card,borderRadius:8,padding:14,marginBottom:12}}>
                  <div style={{color:C.muted,fontSize:11,marginBottom:8,fontWeight:700,letterSpacing:0.8}}>DEMOGRAPHICS</div>
                  <div><b>Name:</b> {selected.demographics.firstName} {selected.demographics.lastName}</div>
                  <div><b>DOB:</b> {selected.demographics.dob||"—"} · Age: {selected.demographics.age||"—"}</div>
                  <div><b>Gender:</b> {selected.demographics.gender||"—"}</div>
                  <div><b>City:</b> {selected.demographics.city||"—"} ({selected.demographics.cityTier||"—"})</div>
                  <div><b>Language:</b> {selected.demographics.language||"—"}</div>
                </div>
              )}
              <div style={{background:C.card,borderRadius:8,padding:14,marginBottom:12}}>
                <div style={{color:C.muted,fontSize:11,marginBottom:8,fontWeight:700,letterSpacing:0.8}}>ACTIVITY</div>
                <div><b>Sessions:</b> {selected.sessions?.length||0}</div>
                <div><b>Invitations:</b> {selected.invitations?.length||0}</div>
                <div><b>Payouts:</b> {selected.payouts?.length||0}</div>
              </div>
              {selected.payouts?.slice(0,5).map(p=>(
                <div key={p.id} style={{background:C.card,borderRadius:6,padding:"8px 12px",marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span>{p.payoutType}</span><span>{inr(p.amount)}</span>
                  <Badge label={p.status} color={p.status==="paid"?"green":p.status==="cancelled"?"red":"yellow"}/>
                </div>
              ))}
            </div>
          )}
        </Drawer>
      )}
    </div>
  );
}

// ── Customers ──────────────────────────────────────────────────────────────────
const INDUSTRIES = ["Technology","Financial Services","Healthcare","FMCG","Retail","E-commerce","Media & Entertainment","Education","Real Estate","Manufacturing","Automotive","Telecom","Government / PSU","Consulting","Other"];
const COMPANY_SIZES = ["1–10","11–50","51–200","201–500","501–1,000","1,001–5,000","5,000+"];
const PLANS = ["starter","professional","enterprise"];

function CustomersScreen({onOpenCenter}) {
  const [rows,setRows]           = useState([]);
  const [total,setTotal]         = useState(0);
  const [search,setSearch]       = useState("");
  const [filterPlan,setFPlan]    = useState("");
  const [filterIndustry,setFInd] = useState("");
  const [filterSize,setFSize]    = useState("");
  const [page,setPage]           = useState(1);
  const [loading,setLoading]     = useState(true);
  const [modal,setModal]         = useState(false);
  const [editCust,setEditCust]   = useState(null);  // null = create, object = edit
  const [form,setForm]           = useState({});
  const [saving,setSaving]       = useState(false);
  const [formMsg,setFormMsg]     = useState("");
  const [delTarget,setDelTarget] = useState(null);  // customer object to delete
  const [delConfirm,setDelConfirm]= useState("");   // typed name
  const [deleting,setDeleting]   = useState(false);
  const [delMsg,setDelMsg]       = useState("");

  const INP = {width:"100%",padding:"8px 10px",borderRadius:6,border:`1px solid ${C.border}`,background:"#0e1117",color:C.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"};
  const SEL = {...INP,cursor:"pointer"};
  const planColor = p=>({starter:"grey",professional:"blue",enterprise:"purple"}[p]||"grey");

  const load = useCallback(()=>{
    setLoading(true);
    const q = new URLSearchParams({
      search, plan: filterPlan, industry: filterIndustry,
      companySize: filterSize, page, limit: 50,
    }).toString();
    api("GET",`/customers?${q}`)
      .then(d=>{setRows(d.customers||[]);setTotal(d.total||0);})
      .catch(()=>{})
      .finally(()=>setLoading(false));
  },[search,filterPlan,filterIndustry,filterSize,page]); // eslint-disable-line

  useEffect(()=>{ const t=setTimeout(load,300); return()=>clearTimeout(t); },[load]); // eslint-disable-line

  function resetFilters() { setSearch(""); setFPlan(""); setFInd(""); setFSize(""); setPage(1); }

  function openCreate() {
    setEditCust(null);
    setForm({plan:"starter",billingCountry:"India"});
    setFormMsg(""); setModal(true);
  }

  async function openEdit(e, c) {
    e.stopPropagation();
    setFormMsg(""); setModal(true);
    setEditCust({id:c.id, companyName:c.companyName}); // placeholder while loading
    setForm({plan:"starter"}); // will be overwritten
    try {
      const data = await api("GET", `/customers/${c.id}/full`);
      const cu = data.customer;
      setEditCust(cu);
      setForm({
        companyName:         cu.companyName         || "",
        industry:            cu.industry            || "",
        companySize:         cu.companySize         || "",
        plan:                cu.plan                || "starter",
        website:             cu.website             || "",
        billingEntityName:   cu.billingEntityName   || "",
        billingAddressLine1: cu.billingAddressLine1 || "",
        billingAddressLine2: cu.billingAddressLine2 || "",
        billingCity:         cu.billingCity         || "",
        billingState:        cu.billingState        || "",
        billingPincode:      cu.billingPincode      || "",
        billingCountry:      cu.billingCountry      || "India",
        gstin:               cu.gstin               || "",
        pan:                 cu.pan                 || "",
        notes:               cu.notes               || "",
      });
    } catch(err) { setFormMsg("Failed to load customer."); }
  }

  async function save() {
    if (!form.companyName?.trim())       { setFormMsg("Company name is required."); return; }
    if (!form.billingEntityName?.trim()) { setFormMsg("Billing entity name is required."); return; }
    if (!form.billingAddressLine1?.trim()){ setFormMsg("Billing address line 1 is required."); return; }
    setSaving(true); setFormMsg("");
    try {
      const payload = { ...form, billingCountry: form.billingCountry || "India" };
      if (editCust) await api("PATCH", `/customers/${editCust.id}`, payload);
      else          await api("POST",  "/customers", payload);
      setModal(false); setEditCust(null); setForm({}); load();
    } catch(e) { setFormMsg(e.message||"Failed to save customer."); }
    finally { setSaving(false); }
  }

  function closeModal() { setModal(false); setEditCust(null); setForm({}); setFormMsg(""); }

  async function openDelete(e, c) {
    e.stopPropagation();
    // Fetch full data to show project/invoice counts in the warning
    setDelMsg(""); setDelConfirm("");
    setDelTarget({ id: c.id, companyName: c.companyName, loading: true });
    try {
      const data = await api("GET", `/customers/${c.id}/full`);
      const cu = data.customer;
      const activeProjects = (cu.projects||[]).filter(p=>p.status==="active").length;
      const totalProjects  = (cu.projects||[]).length;
      const unpaidInvoices = (cu.invoices||[]).filter(i=>i.status!=="paid"&&i.status!=="cancelled").length;
      const totalInvoices  = (cu.invoices||[]).length;
      setDelTarget({ id: cu.id, companyName: cu.companyName, activeProjects, totalProjects, unpaidInvoices, totalInvoices });
    } catch { setDelTarget(t=>({...t, loading: false, fetchError: true})); }
  }

  async function confirmDelete() {
    if (!delTarget) return;
    if (delConfirm.trim().toLowerCase() !== delTarget.companyName.toLowerCase()) {
      setDelMsg("Company name does not match. Please type it exactly."); return;
    }
    setDeleting(true); setDelMsg("");
    try {
      await api("DELETE", `/customers/${delTarget.id}`, { confirmName: delConfirm.trim() });
      setDelTarget(null); setDelConfirm(""); load();
    } catch(e) { setDelMsg(e.message||"Failed to delete customer."); }
    finally { setDeleting(false); }
  }

  const hasFilters = search||filterPlan||filterIndustry||filterSize;

  return (
    <div>
      {/* ── Filter bar ── */}
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
        <input
          style={{...INP,flex:"1 1 220px",maxWidth:300}}
          placeholder="Search by company name…"
          value={search}
          onChange={e=>{setSearch(e.target.value);setPage(1);}}
        />
        <select style={{...SEL,flex:"0 0 140px"}} value={filterPlan} onChange={e=>{setFPlan(e.target.value);setPage(1);}}>
          <option value="">All Plans</option>
          {PLANS.map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
        </select>
        <select style={{...SEL,flex:"0 0 160px"}} value={filterIndustry} onChange={e=>{setFInd(e.target.value);setPage(1);}}>
          <option value="">All Industries</option>
          {INDUSTRIES.map(i=><option key={i} value={i}>{i}</option>)}
        </select>
        <select style={{...SEL,flex:"0 0 140px"}} value={filterSize} onChange={e=>{setFSize(e.target.value);setPage(1);}}>
          <option value="">All Sizes</option>
          {COMPANY_SIZES.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        {hasFilters&&<button style={{background:"transparent",border:"none",color:C.muted,cursor:"pointer",fontSize:12,padding:"4px 8px"}} onClick={resetFilters}>✕ Clear</button>}
        <button
          style={{marginLeft:"auto",padding:"8px 18px",borderRadius:8,border:"none",background:C.accent,color:"#000",fontWeight:700,fontSize:13,cursor:"pointer",flexShrink:0}}
          onClick={openCreate}
        >+ New Customer</button>
      </div>

      {loading?<Spinner/>:(
        <>
          <TableWrap>
            <table className="rc-table"><thead className="rc-thead"><tr className="rc-tr">
              <Th>Company</Th><Th>Industry</Th><Th>Size</Th><Th>Plan</Th><Th>Surveys</Th><Th>Joined</Th><Th></Th>
            </tr></thead>
            <tbody className="rc-tbody">
              {rows.length===0&&<tr className="rc-tr"><td colSpan={7}><EmptyState message="No customers found"/></td></tr>}
              {rows.map(c=>(
                <tr key={c.id} className="rc-tr" style={{cursor:"pointer"}} onClick={()=>onOpenCenter(c.id)}>
                  <Td label="Company">
                    <span style={{fontWeight:600,color:C.accent}}>{c.companyName}</span>
                    {c.billingEntityName&&c.billingEntityName!==c.companyName&&
                      <div style={{fontSize:11,color:C.muted,marginTop:2}}>{c.billingEntityName}</div>}
                  </Td>
                  <Td label="Industry" style={{color:C.muted}}>{c.industry||"—"}</Td>
                  <Td label="Size" style={{color:C.muted}}>{c.companySize||"—"}</Td>
                  <Td label="Plan"><Badge label={c.plan} color={planColor(c.plan)}/></Td>
                  <Td label="Surveys">{c._count?.surveys||0}</Td>
                  <Td label="Joined" style={{color:C.muted}}>{fmt(c.createdAt)}</Td>
                  <Td className="rc-td rc-td-actions">
                    <div style={{display:"flex",gap:6}}>
                      <button
                        onClick={e=>openEdit(e,c)}
                        style={{padding:"4px 12px",borderRadius:6,border:`1px solid ${C.border}`,background:"transparent",color:C.muted,fontSize:12,cursor:"pointer"}}
                        onMouseEnter={e=>e.currentTarget.style.color=C.text}
                        onMouseLeave={e=>e.currentTarget.style.color=C.muted}
                      >Edit</button>
                      <button
                        onClick={e=>openDelete(e,c)}
                        style={{padding:"4px 12px",borderRadius:6,border:`1px solid ${C.danger}22`,background:"transparent",color:C.danger,fontSize:12,cursor:"pointer",opacity:0.7}}
                        onMouseEnter={e=>e.currentTarget.style.opacity="1"}
                        onMouseLeave={e=>e.currentTarget.style.opacity="0.7"}
                      >Delete</button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody></table>
          </TableWrap>
          <Pagination page={page} total={total} limit={50} onPage={setPage}/>
        </>
      )}

      {/* ── Create / Edit Customer Modal ── */}
      {modal&&(
        <div style={{position:"fixed",inset:0,background:"#000b",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:28,width:"100%",maxWidth:580,maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <span style={{fontWeight:700,fontSize:16,color:C.text}}>{editCust?"Edit Customer":"New Customer"}</span>
              <button onClick={closeModal} style={{background:"transparent",border:"none",color:C.muted,fontSize:20,cursor:"pointer",lineHeight:1}}>×</button>
            </div>

            {/* Company info */}
            <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Company</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>COMPANY NAME *</label>
                <input style={INP} value={form.companyName||""} onChange={e=>setForm(p=>({...p,companyName:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>INDUSTRY</label>
                <select style={SEL} value={form.industry||""} onChange={e=>setForm(p=>({...p,industry:e.target.value}))}>
                  <option value="">— Select —</option>
                  {INDUSTRIES.map(i=><option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>COMPANY SIZE</label>
                <select style={SEL} value={form.companySize||""} onChange={e=>setForm(p=>({...p,companySize:e.target.value}))}>
                  <option value="">— Select —</option>
                  {COMPANY_SIZES.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>PLAN</label>
                <select style={SEL} value={form.plan||"starter"} onChange={e=>setForm(p=>({...p,plan:e.target.value}))}>
                  {PLANS.map(pl=><option key={pl} value={pl}>{pl.charAt(0).toUpperCase()+pl.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>WEBSITE</label>
                <input style={INP} placeholder="https://…" value={form.website||""} onChange={e=>setForm(p=>({...p,website:e.target.value}))}/>
              </div>
            </div>

            {/* Billing */}
            <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8,marginTop:16,paddingTop:14,borderTop:`1px solid ${C.border}`}}>Billing</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>BILLING ENTITY NAME *</label>
                <input style={INP} placeholder="Legal entity name for invoices" value={form.billingEntityName||""} onChange={e=>setForm(p=>({...p,billingEntityName:e.target.value}))}/>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>ADDRESS LINE 1 *</label>
                <input style={INP} value={form.billingAddressLine1||""} onChange={e=>setForm(p=>({...p,billingAddressLine1:e.target.value}))}/>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>ADDRESS LINE 2</label>
                <input style={INP} placeholder="Floor, building, landmark…" value={form.billingAddressLine2||""} onChange={e=>setForm(p=>({...p,billingAddressLine2:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>CITY</label>
                <input style={INP} value={form.billingCity||""} onChange={e=>setForm(p=>({...p,billingCity:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>STATE</label>
                <input style={INP} value={form.billingState||""} onChange={e=>setForm(p=>({...p,billingState:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>PINCODE</label>
                <input style={INP} value={form.billingPincode||""} onChange={e=>setForm(p=>({...p,billingPincode:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>COUNTRY</label>
                <input style={INP} value={form.billingCountry||"India"} onChange={e=>setForm(p=>({...p,billingCountry:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>GSTIN</label>
                <input style={INP} placeholder="e.g. 29AABCT1332L1ZN" value={form.gstin||""} onChange={e=>setForm(p=>({...p,gstin:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>PAN</label>
                <input style={INP} placeholder="e.g. AABCT1332L" value={form.pan||""} onChange={e=>setForm(p=>({...p,pan:e.target.value}))}/>
              </div>
            </div>

            {formMsg&&<div style={{color:C.danger,fontSize:13,marginBottom:10}}>{formMsg}</div>}
            <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:8}}>
              <button onClick={closeModal} style={{padding:"8px 18px",borderRadius:8,border:`1px solid ${C.border}`,background:"transparent",color:C.muted,cursor:"pointer",fontSize:13}}>Cancel</button>
              <button onClick={save} disabled={saving} style={{padding:"8px 20px",borderRadius:8,border:"none",background:C.accent,color:"#000",fontWeight:700,fontSize:13,cursor:saving?"not-allowed":"pointer",opacity:saving?0.7:1}}>
                {saving?(editCust?"Saving…":"Creating…"):(editCust?"Save Changes":"Create Customer")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Customer Confirmation Modal ── */}
      {delTarget&&(
        <div style={{position:"fixed",inset:0,background:"#000c",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:C.card,border:`2px solid ${C.danger}`,borderRadius:12,padding:28,width:"100%",maxWidth:480}}>
            {/* Header */}
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:`${C.danger}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>&#9888;</div>
              <div>
                <div style={{fontWeight:700,fontSize:16,color:C.danger}}>Delete Customer</div>
                <div style={{fontSize:12,color:C.muted,marginTop:2}}>This action is permanent and cannot be undone.</div>
              </div>
            </div>

            {delTarget.loading ? (
              <div style={{textAlign:"center",padding:"20px 0",color:C.muted,fontSize:13}}>Loading customer data…</div>
            ) : delTarget.fetchError ? (
              <div style={{color:C.danger,fontSize:13,marginBottom:16}}>Could not load customer details. Deletion aborted for safety.</div>
            ) : (
              <>
                {/* Impact summary */}
                <div style={{background:`${C.danger}11`,border:`1px solid ${C.danger}44`,borderRadius:8,padding:"12px 14px",marginBottom:16}}>
                  <div style={{fontWeight:600,fontSize:13,color:C.text,marginBottom:8}}>
                    Deleting <span style={{color:C.danger}}>{delTarget.companyName}</span> will permanently erase:
                  </div>
                  <div style={{fontSize:13,color:C.muted,lineHeight:1.8}}>
                    <div>&#8226; {delTarget.totalProjects} project{delTarget.totalProjects!==1?"s":""}{delTarget.activeProjects>0&&<span style={{color:C.danger,fontWeight:600}}> ({delTarget.activeProjects} currently active)</span>}</div>
                    <div>&#8226; {delTarget.totalInvoices} invoice{delTarget.totalInvoices!==1?"s":""}{delTarget.unpaidInvoices>0&&<span style={{color:C.danger,fontWeight:600}}> ({delTarget.unpaidInvoices} unpaid / outstanding)</span>}</div>
                    <div>&#8226; All contacts, documents, surveys, sessions, and portal users</div>
                    <div>&#8226; All associated participant session data and analytics</div>
                  </div>
                </div>

                {(delTarget.activeProjects > 0 || delTarget.unpaidInvoices > 0) && (
                  <div style={{background:`${C.danger}18`,border:`1px solid ${C.danger}`,borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:13,color:C.danger,fontWeight:600}}>
                    Warning: This customer has {delTarget.activeProjects > 0 ? `${delTarget.activeProjects} active project${delTarget.activeProjects!==1?"s":""}` : ""}{delTarget.activeProjects > 0 && delTarget.unpaidInvoices > 0 ? " and " : ""}{delTarget.unpaidInvoices > 0 ? `${delTarget.unpaidInvoices} unpaid invoice${delTarget.unpaidInvoices!==1?"s":""}` : ""}. Ensure all obligations are settled before deleting.
                  </div>
                )}

                {/* Name confirmation */}
                <div style={{marginBottom:16}}>
                  <label style={{fontSize:12,color:C.muted,display:"block",marginBottom:6}}>
                    Type <strong style={{color:C.text}}>{delTarget.companyName}</strong> to confirm deletion:
                  </label>
                  <input
                    style={{width:"100%",padding:"9px 12px",borderRadius:7,border:`1px solid ${delConfirm&&delConfirm.trim().toLowerCase()===delTarget.companyName.toLowerCase()?C.danger:C.border}`,background:"#0e1117",color:C.text,fontSize:14,fontFamily:"inherit",boxSizing:"border-box"}}
                    placeholder={delTarget.companyName}
                    value={delConfirm}
                    onChange={e=>{setDelConfirm(e.target.value);setDelMsg("");}}
                    autoFocus
                  />
                </div>

                {delMsg&&<div style={{color:C.danger,fontSize:13,marginBottom:10}}>{delMsg}</div>}

                <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                  <button
                    onClick={()=>{setDelTarget(null);setDelConfirm("");setDelMsg("");}}
                    style={{padding:"8px 18px",borderRadius:8,border:`1px solid ${C.border}`,background:"transparent",color:C.muted,cursor:"pointer",fontSize:13}}
                  >Cancel</button>
                  <button
                    onClick={confirmDelete}
                    disabled={deleting||delConfirm.trim().toLowerCase()!==delTarget.companyName.toLowerCase()}
                    style={{padding:"8px 20px",borderRadius:8,border:"none",background:C.danger,color:"#fff",fontWeight:700,fontSize:13,cursor:(deleting||delConfirm.trim().toLowerCase()!==delTarget.companyName.toLowerCase())?"not-allowed":"pointer",opacity:(deleting||delConfirm.trim().toLowerCase()!==delTarget.companyName.toLowerCase())?0.4:1,transition:"opacity 0.2s"}}
                  >{deleting?"Deleting…":"Delete Permanently"}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Surveys ────────────────────────────────────────────────────────────────────
function SurveysScreen({onOpenSurvey}) {
  const [rows,setRows]=useState([]); const [total,setTotal]=useState(0);
  const [page,setPage]=useState(1); const [loading,setLoading]=useState(true);
  // filters
  const [fTitle,setFTitle]=useState("");
  const [fCustomer,setFCustomer]=useState("");
  const [fProject,setFProject]=useState("");
  const [fModality,setFModality]=useState("");
  const [fStatus,setFStatus]=useState("");
  const [fDate,setFDate]=useState("");
  // dropdown data
  const [customers,setCustomers]=useState([]);
  const [projects,setProjects]=useState([]);

  useEffect(()=>{ api("GET","/customers?limit=200").then(d=>setCustomers(d.customers||[])).catch(()=>{}); },[]);
  useEffect(()=>{
    if(!fCustomer){setProjects([]);setFProject("");return;}
    api("GET",`/customers/${fCustomer}/projects`).then(d=>setProjects(d.projects||[])).catch(()=>{});
    setFProject("");
  },[fCustomer]);

  const load=useCallback(()=>{
    setLoading(true);
    const p=new URLSearchParams({page,limit:50});
    if(fTitle.trim()) p.set("search",fTitle.trim());
    if(fCustomer)     p.set("customerId",fCustomer);
    if(fProject)      p.set("projectId",fProject);
    if(fModality)     p.set("modality",fModality);
    if(fStatus)       p.set("status",fStatus);
    if(fDate){
      const offs={"7d":7,"14d":14,"30d":30,"3m":91,"6m":182,"1y":365};
      const days=offs[fDate]; if(days){const d=new Date();d.setDate(d.getDate()-days);p.set("createdAfter",d.toISOString());}
    }
    api("GET",`/surveys?${p.toString()}`)
      .then(d=>{setRows(d.surveys||[]);setTotal(d.total||0);}).catch(()=>{}).finally(()=>setLoading(false));
  },[fTitle,fCustomer,fProject,fModality,fStatus,fDate,page]);
  useEffect(()=>{load();},[load]);

  function clearAll(){setFTitle("");setFCustomer("");setFProject("");setFModality("");setFStatus("");setFDate("");setPage(1);}
  const hasFilter=fTitle||fCustomer||fProject||fModality||fStatus||fDate;

  const statusColor=s=>({active:"green",closed:"grey",draft:"yellow",pending_review:"orange",approved:"green"}[s]||"grey");
  const modalityColor=m=>({video:"purple",voice:"blue",chat:"green"}[m]||"grey");
  const SEL={padding:"7px 10px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,fontSize:13,fontFamily:"inherit",outline:"none",cursor:"pointer",minWidth:0};
  const INP={...SEL,cursor:"text"};

  return (
    <div>
      {/* ── Filter bar ── */}
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 16px",marginBottom:16,display:"flex",flexDirection:"column",gap:8}}>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <input value={fTitle} onChange={e=>{setFTitle(e.target.value);setPage(1);}} placeholder="Search by title…"
            style={{...INP,flex:"2 1 200px"}}/>
          <select value={fCustomer} onChange={e=>{setFCustomer(e.target.value);setPage(1);}} style={{...SEL,flex:"1 1 160px"}}>
            <option value="">All Customers</option>
            {customers.map(c=><option key={c.id} value={c.id}>{c.companyName}</option>)}
          </select>
          <select value={fProject} onChange={e=>{setFProject(e.target.value);setPage(1);}} style={{...SEL,flex:"1 1 160px",opacity:fCustomer?1:0.45}} disabled={!fCustomer}>
            <option value="">{fCustomer?"All Projects":"Select customer first"}</option>
            {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <select value={fModality} onChange={e=>{setFModality(e.target.value);setPage(1);}} style={{...SEL,flex:"1 1 140px"}}>
            <option value="">All Modalities</option>
            <option value="video">Video</option>
            <option value="voice">Voice</option>
            <option value="chat">Chat</option>
          </select>
          <select value={fStatus} onChange={e=>{setFStatus(e.target.value);setPage(1);}} style={{...SEL,flex:"1 1 160px"}}>
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="pending_review">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </select>
          <select value={fDate} onChange={e=>{setFDate(e.target.value);setPage(1);}} style={{...SEL,flex:"1 1 160px"}}>
            <option value="">All time</option>
            <option value="7d">Last 7 days</option>
            <option value="14d">Last 14 days</option>
            <option value="30d">Last 30 days</option>
            <option value="3m">Last 3 months</option>
            <option value="6m">Last 6 months</option>
            <option value="1y">Last year</option>
          </select>
          {hasFilter&&<button onClick={clearAll} style={{padding:"7px 14px",background:"transparent",border:`1px solid ${C.border}`,borderRadius:6,color:C.muted,fontSize:12,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",flexShrink:0}}>✕ Clear</button>}
        </div>
      </div>

      {loading?<Spinner/>:(
        <>
          <TableWrap>
            <table className="rc-table"><thead className="rc-thead"><tr className="rc-tr"><Th>Title</Th><Th>Customer</Th><Th>Project</Th><Th>Modality</Th><Th>Status</Th><Th>Invitations</Th><Th>Sessions</Th><Th>Created</Th></tr></thead>
            <tbody className="rc-tbody">
              {rows.length===0&&<tr className="rc-tr"><td colSpan={8}><EmptyState message="No surveys found"/></td></tr>}
              {rows.map(s=>(
                <tr key={s.id} className="rc-tr" style={{cursor:s.customer?.id?"pointer":"default"}}
                  onClick={()=>s.customer?.id&&onOpenSurvey&&onOpenSurvey(s)}
                  onMouseEnter={e=>{if(s.customer?.id)e.currentTarget.style.background=C.surface;}}
                  onMouseLeave={e=>{e.currentTarget.style.background="";}}>
                  <Td label="Title"><span style={{fontWeight:600}}>{s.title}</span></Td>
                  <Td label="Customer" style={{color:C.muted}}>{s.customer?.companyName||"—"}</Td>
                  <Td label="Project" style={{fontSize:12}}>{s.project?.name||<span style={{color:C.danger,fontSize:11}}>No project</span>}</Td>
                  <Td label="Modality"><Badge label={s.modality} color={modalityColor(s.modality)}/></Td>
                  <Td label="Status"><Badge label={s.status} color={statusColor(s.status)}/></Td>
                  <Td label="Invitations">{s._count?.invitations||0}</Td>
                  <Td label="Sessions">{s._count?.sessions||0}</Td>
                  <Td label="Created" style={{color:C.muted}}>{fmt(s.createdAt)}</Td>
                </tr>
              ))}
            </tbody></table>
          </TableWrap>
          <Pagination page={page} total={total} limit={50} onPage={setPage}/>
        </>
      )}
    </div>
  );
}

// ── Payouts ────────────────────────────────────────────────────────────────────

const PAYOUT_TYPE_LABELS = {
  survey:                 "Survey",
  referral_referrer_1:    "Ref Reward 1",
  referral_referrer_2:    "Ref Reward 2",
  referral_referee_bonus: "Ref Bonus",
};

const PAYOUT_TYPE_OPTIONS = [
  {value:"",label:"All Types"},
  {value:"survey",label:"Survey"},
  {value:"referral_referrer_1",label:"Referral Reward 1"},
  {value:"referral_referrer_2",label:"Referral Reward 2"},
  {value:"referral_referee_bonus",label:"Referral Bonus"},
];

function UpiChip({detail}){
  if(!detail || !detail.hasUpi) return <span style={{color:C.muted,fontSize:11}}>No UPI</span>;
  if(detail.upiVerified) return <span style={{fontSize:11,color:C.success}}>✓ Verified</span>;
  return <span style={{fontSize:11,color:C.accent}}>⚠ {detail.upiVerificationStatus||"unverified"}</span>;
}

const QC_FLAG_COLOR = { pass:"green", review:"yellow", fail:"red" };
// Reason codes are SCREAMING_SNAKE — render them as readable phrases without
// maintaining a label map that drifts from the scorer (e.g. DURATION_TOO_SHORT
// → "Duration too short").
const prettyReason = c => c.replace(/_/g," ").toLowerCase().replace(/^\w/, m=>m.toUpperCase());
const fmtDur = secs => secs==null ? "—" : (secs>=60 ? `${Math.floor(secs/60)}m ${secs%60}s` : `${secs}s`);

function PayoutsScreen() {
  const [rows,setRows]           = useState([]);
  const [total,setTotal]         = useState(0);
  const [statusFilter,setStatus] = useState("under_review");
  const [typeFilter,setType]     = useState("");
  const [page,setPage]           = useState(1);
  const [loading,setLoading]     = useState(true);
  const [expanded,setExpanded]   = useState(null);
  const [modal,setModal]         = useState(null); // {id,action,label,payout}
  const [note,setNote]           = useState("");
  const [txnId,setTxnId]         = useState("");
  const [acting,setActing]       = useState(false);
  const [msg,setMsg]             = useState("");
  const [warn,setWarn]           = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const qs = [
      statusFilter !== "all" ? `status=${statusFilter}` : "",
      typeFilter ? `payoutType=${typeFilter}` : "",
      `page=${page}`,
      "limit=50",
    ].filter(Boolean).join("&");
    api("GET", `/payouts?${qs}`)
      .then(d => { setRows(d.payouts||[]); setTotal(d.total||0); })
      .catch(()=>{})
      .finally(()=>setLoading(false));
  }, [statusFilter, typeFilter, page]);

  useEffect(() => { load(); }, [load]);

  function openModal(payout, action, label) {
    setModal({id:payout.id, action, label, payout});
    setNote(""); setTxnId(payout.pspReference||""); setWarn("");
  }

  async function changeStatus() {
    setActing(true); setWarn("");
    try {
      const body = { status: modal.action, adminNote: note || undefined };
      if (modal.action === "paid") body.pspReference = txnId || undefined;
      const res = await api("PATCH", `/payouts/${modal.id}/status`, body);
      if (res.error) { setWarn(res.error); return; }
      if (res.warning) setWarn(res.warning);
      else { setModal(null); setNote(""); setTxnId(""); }
      setMsg("Updated"); load(); setTimeout(()=>setMsg(""),2500);
    } catch(e) { setWarn(e.message); }
    finally { setActing(false); }
  }

  const statusColor = s => ({under_review:"yellow",pending_payout:"blue",on_hold:"grey",paid:"green",cancelled:"red"}[s]||"grey");
  const typeColor   = t => ({survey:"blue",referral_referrer_1:"purple",referral_referrer_2:"purple",referral_referee_bonus:"green"}[t]||"grey");

  function sourceLabel(p) {
    if (p.payoutType === "survey") return p.survey?.title || "—";
    if (p.referral) return `Referral: ${p.referral.referrer?.name||"?"} → ${p.referral.referee?.name||"?"}`;
    return "Referral";
  }

  const STATUS_TABS = [
    {value:"under_review",label:"Under Review"},
    {value:"pending_payout",label:"Pending Payout"},
    {value:"on_hold",label:"On Hold"},
    {value:"paid",label:"Paid"},
    {value:"cancelled",label:"Cancelled"},
    {value:"all",label:"All"},
  ];

  return (
    <div>
      {msg&&<div style={{marginBottom:12,color:C.success,fontSize:13}}>{msg}</div>}

      {/* Status tabs + type filter */}
      <div style={{display:"flex",alignItems:"center",flexWrap:"wrap",gap:10,marginBottom:16}}>
        <FilterTabs tabs={STATUS_TABS} active={statusFilter} onChange={v=>{setStatus(v);setPage(1);}}/>
        <select value={typeFilter} onChange={e=>{setType(e.target.value);setPage(1);}}
          style={{marginLeft:"auto",background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,
            color:C.text,padding:"5px 10px",fontSize:13,cursor:"pointer"}}>
          {PAYOUT_TYPE_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {loading?<Spinner/>:(
        <>
          <TableWrap>
            <table className="rc-table">
              <thead className="rc-thead">
                <tr className="rc-tr">
                  <Th>Participant</Th><Th>Source</Th><Th>Amount</Th>
                  <Th>Type</Th><Th>Status</Th><Th>Transaction ID</Th>
                  <Th>Date</Th><Th>Actions</Th>
                </tr>
              </thead>
              <tbody className="rc-tbody">
                {rows.length===0&&<tr className="rc-tr"><td colSpan={8}><EmptyState message="No payouts found"/></td></tr>}
                {rows.map(p=>(
                  <React.Fragment key={p.id}>
                    <tr className="rc-tr" style={{cursor:"pointer"}} onClick={()=>setExpanded(expanded===p.id?null:p.id)}>
                      <Td label="Participant">
                        <div style={{fontWeight:600}}>{p.participant?.name}</div>
                        <div style={{color:C.muted,fontSize:11}}>{p.participant?.email}</div>
                        <UpiChip detail={p.participant?.paymentDetail}/>
                      </Td>
                      <Td label="Source" style={{color:C.muted,fontSize:12,maxWidth:160}}>{sourceLabel(p)}</Td>
                      <Td label="Amount"><span style={{fontWeight:700,color:C.accent}}>{inr(p.amount)}</span></Td>
                      <Td label="Type"><Badge label={PAYOUT_TYPE_LABELS[p.payoutType]||p.payoutType} color={typeColor(p.payoutType)}/></Td>
                      <Td label="Status">
                        <Badge label={p.status.replace(/_/g," ")} color={statusColor(p.status)}/>
                        {p.qc&&<div style={{marginTop:4}}>
                          <Badge label={`QC ${p.qc.flag}${p.qc.score!=null?" · "+Math.round(p.qc.score):""}`} color={QC_FLAG_COLOR[p.qc.flag]||"grey"}/>
                        </div>}
                      </Td>
                      <Td label="Txn ID">
                        {p.pspReference
                          ? <span style={{fontFamily:"monospace",fontSize:12,color:C.text,cursor:"pointer"}}
                              title="Click to copy"
                              onClick={e=>{e.stopPropagation();navigator.clipboard?.writeText(p.pspReference);}}>
                              {p.pspReference}
                            </span>
                          : <span style={{color:C.muted}}>—</span>}
                      </Td>
                      <Td label="Date" style={{color:C.muted,fontSize:12}}>
                        {p.status==="paid"?fmt(p.paidAt):fmt(p.createdAt)}
                      </Td>
                      <Td className="rc-td rc-td-actions" onClick={e=>e.stopPropagation()}>
                        <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                          {p.status==="under_review"&&<>
                            <Btn variant="smSuccess" onClick={()=>openModal(p,"pending_payout","Approve")}>Approve</Btn>
                            <Btn variant="sm"        onClick={()=>openModal(p,"on_hold","Hold")}>Hold</Btn>
                            <Btn variant="smDanger"  onClick={()=>openModal(p,"cancelled","Cancel")}>Cancel</Btn>
                          </>}
                          {p.status==="pending_payout"&&<>
                            <Btn variant="sm"       onClick={()=>openModal(p,"paid","Mark Paid")}>Mark Paid</Btn>
                            <Btn variant="smDanger" onClick={()=>openModal(p,"cancelled","Cancel")}>Cancel</Btn>
                          </>}
                          {p.status==="on_hold"&&<>
                            <Btn variant="smSuccess" onClick={()=>openModal(p,"pending_payout","Release")}>Release</Btn>
                            <Btn variant="smDanger"  onClick={()=>openModal(p,"cancelled","Cancel")}>Cancel</Btn>
                          </>}
                        </div>
                      </Td>
                    </tr>

                    {/* Expanded detail row */}
                    {expanded===p.id&&(
                      <tr className="rc-tr" style={{background:C.surface}} onClick={e=>e.stopPropagation()}>
                        <td colSpan={8} style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,display:"block"}}>
                          <div style={{display:"flex",flexWrap:"wrap",gap:24,fontSize:13}}>
                            {/* Quality Check context — the basis for approve / hold / cancel */}
                            {p.qc&&<div style={{flexBasis:"100%",padding:"10px 12px",background:C.bg,borderRadius:8,border:`1px solid ${C.border}`}}>
                              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:8}}>
                                <span style={{color:C.muted,fontSize:11,fontWeight:600,letterSpacing:0.5}}>QUALITY CHECK</span>
                                <Badge label={`${p.qc.flag}${p.qc.score!=null?" · "+Math.round(p.qc.score):""}`} color={QC_FLAG_COLOR[p.qc.flag]||"grey"}/>
                                <span style={{color:C.muted,fontSize:12}}>Duration {fmtDur(p.qc.durationSecs)}</span>
                                {p.qc.adminReviewStatus&&(p.qc.flag==="review"||p.qc.flag==="fail")&&<span style={{color:C.muted,fontSize:12}}>· Session review: {p.qc.adminReviewStatus}</span>}
                              </div>
                              {p.qc.reasonCodes?.length>0
                                ? <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{p.qc.reasonCodes.map(c=><Badge key={c} label={prettyReason(c)} color="grey"/>)}</div>
                                : <div style={{color:C.muted,fontSize:12}}>No quality flags raised.</div>}
                              {p.qc.adminReviewNote&&<div style={{marginTop:6,color:C.muted,fontSize:12}}>Review note: {p.qc.adminReviewNote}</div>}
                            </div>}
                            <div>
                              <div style={{color:C.muted,fontSize:11,marginBottom:4}}>CREATED</div>
                              <div>{fmt(p.createdAt)}</div>
                            </div>
                            {p.reviewedAt&&<div>
                              <div style={{color:C.muted,fontSize:11,marginBottom:4}}>APPROVED / HELD</div>
                              <div>{fmt(p.reviewedAt)}</div>
                            </div>}
                            {p.paidAt&&<div>
                              <div style={{color:C.muted,fontSize:11,marginBottom:4}}>PAID</div>
                              <div style={{color:C.success}}>{fmt(p.paidAt)}</div>
                            </div>}
                            {p.cancelledAt&&<div>
                              <div style={{color:C.muted,fontSize:11,marginBottom:4}}>CANCELLED</div>
                              <div style={{color:C.danger}}>{fmt(p.cancelledAt)}</div>
                            </div>}
                            {p.updatedBy&&<div>
                              <div style={{color:C.muted,fontSize:11,marginBottom:4}}>LAST UPDATED BY</div>
                              <div>{p.updatedBy}</div>
                            </div>}
                            {p.pspReference&&<div>
                              <div style={{color:C.muted,fontSize:11,marginBottom:4}}>TRANSACTION ID</div>
                              <div style={{fontFamily:"monospace"}}>{p.pspReference}
                                <button onClick={()=>navigator.clipboard?.writeText(p.pspReference)}
                                  style={{marginLeft:6,background:"none",border:"none",color:C.info,cursor:"pointer",fontSize:11}}>
                                  Copy
                                </button>
                              </div>
                            </div>}
                            {p.adminNote&&<div style={{flex:"1 1 200px"}}>
                              <div style={{color:C.muted,fontSize:11,marginBottom:4}}>ADMIN NOTE</div>
                              <div style={{color:C.muted}}>{p.adminNote}</div>
                            </div>}
                            {p.referral&&<div>
                              <div style={{color:C.muted,fontSize:11,marginBottom:4}}>REFERRAL</div>
                              <div>{p.referral.referrer?.name} → {p.referral.referee?.name}</div>
                              <div style={{color:C.muted,fontSize:11}}>status: {p.referral.status}</div>
                            </div>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </TableWrap>
          <Pagination page={page} total={total} limit={50} onPage={setPage}/>
        </>
      )}

      {/* Status change modal */}
      {modal&&(
        <Modal title={`${modal.label} Payout`} onClose={()=>{setModal(null);setNote("");setTxnId("");setWarn("");}}>
          <div style={{marginBottom:12,fontSize:13,color:C.muted}}>
            <span style={{fontWeight:600,color:C.text}}>{modal.payout.participant?.name}</span>
            {" · "}{inr(modal.payout.amount)}
          </div>

          {/* Hard block: no UPI on file — disbursement is impossible */}
          {modal.action==="paid"&&!modal.payout.participant?.paymentDetail?.hasUpi&&(
            <div style={{marginBottom:12,padding:"8px 12px",background:C.danger+"22",borderRadius:6,fontSize:12,color:C.danger}}>
              ⛔ No UPI ID on file — this participant can't be paid yet. Ask them to add their UPI ID, or put the payout on hold.
            </div>
          )}

          {/* Soft warning: UPI present but not penny-drop verified */}
          {modal.action==="paid"&&modal.payout.participant?.paymentDetail?.hasUpi&&!modal.payout.participant.paymentDetail.upiVerified&&(
            <div style={{marginBottom:12,padding:"8px 12px",background:C.accent+"22",borderRadius:6,fontSize:12,color:C.accent}}>
              ⚠ UPI not verified — confirm you have the correct UPI ID before proceeding.
            </div>
          )}

          {/* Transaction ID field (shown only for Mark Paid) */}
          {modal.action==="paid"&&(
            <div style={{marginBottom:12}}>
              <div style={{color:C.muted,fontSize:11,marginBottom:4}}>UPI TRANSACTION ID / UTR</div>
              <input
                value={txnId}
                onChange={e=>setTxnId(e.target.value)}
                placeholder="Enter UTR or transaction reference…"
                style={{width:"100%",padding:"7px 10px",background:C.surface,border:`1px solid ${C.border}`,
                  borderRadius:6,color:C.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}
              />
              {!txnId&&<div style={{color:C.muted,fontSize:11,marginTop:4}}>Optional but strongly recommended for reconciliation.</div>}
            </div>
          )}

          <div style={{marginBottom:16}}>
            <div style={{color:C.muted,fontSize:11,marginBottom:4}}>ADMIN NOTE (OPTIONAL)</div>
            <textarea value={note} onChange={e=>setNote(e.target.value)}
              placeholder="e.g. Paid via PhonePe, TDS pending, duplicate…"
              rows={2}
              style={{width:"100%",padding:"8px 10px",background:C.surface,border:`1px solid ${C.border}`,
                borderRadius:6,color:C.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box",resize:"vertical"}}/>
          </div>

          {warn&&<div style={{marginBottom:12,fontSize:12,color:modal.action==="paid"?C.accent:C.danger}}>{warn}</div>}

          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>{setModal(null);setNote("");setTxnId("");setWarn("");}}>Cancel</Btn>
            <Btn
              variant={modal.action==="cancelled"?"danger":modal.action==="paid"?"success":"primary"}
              disabled={acting || (modal.action==="paid" && !modal.payout.participant?.paymentDetail?.hasUpi)}
              onClick={changeStatus}>
              {acting?"…":modal.label}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Referrals ──────────────────────────────────────────────────────────────────
function ReferralsScreen() {
  const [rows,setRows]=useState([]); const [total,setTotal]=useState(0);
  const [statusFilter,setStatusFilter]=useState("flagged"); const [page,setPage]=useState(1); const [loading,setLoading]=useState(true);
  const [acting,setActing]=useState(null); const [msg,setMsg]=useState("");

  const load=useCallback(()=>{
    setLoading(true);
    api("GET",`/referrals?status=${statusFilter}&page=${page}&limit=50`)
      .then(d=>{setRows(d.referrals||[]);setTotal(d.total||0);}).catch(()=>{}).finally(()=>setLoading(false));
  },[statusFilter,page]);
  useEffect(()=>{load();},[load]);

  async function doAction(id,action) {
    setActing(id+action);
    try { await api("PATCH",`/referrals/${id}/status`,{action}); setMsg(`Referral ${action}d`); load(); setTimeout(()=>setMsg(""),2500); }
    catch(e){ setMsg(e.message); } finally { setActing(null); }
  }

  const statusColor=s=>({pending:"grey",flagged:"red",milestone_1:"blue",milestone_2_rewarded:"green",expired:"grey",rejected:"red"}[s]||"grey");

  return (
    <div>
      {msg&&<div style={{marginBottom:12,color:C.success,fontSize:13}}>{msg}</div>}
      <FilterTabs tabs={[{value:"flagged",label:"Flagged"},{value:"pending",label:"Pending"},{value:"all",label:"All"}]} active={statusFilter} onChange={v=>{setStatusFilter(v);setPage(1);}}/>
      {loading?<Spinner/>:(
        <>
          <TableWrap>
            <table className="rc-table"><thead className="rc-thead"><tr className="rc-tr"><Th>Referrer</Th><Th>Referee</Th><Th>Fraud Flags</Th><Th>Registered</Th><Th>Status</Th><Th>Actions</Th></tr></thead>
            <tbody className="rc-tbody">
              {rows.length===0&&<tr className="rc-tr"><td colSpan={6}><EmptyState message="No referrals found"/></td></tr>}
              {rows.map(r=>{
                let flags=[]; try{flags=JSON.parse(r.fraudFlags||"[]");}catch{}
                return (
                  <tr key={r.id} className="rc-tr">
                    <Td label="Referrer"><div style={{fontWeight:600}}>{r.referrer?.name}</div><div style={{color:C.muted,fontSize:11}}>{r.referrer?.email}</div></Td>
                    <Td label="Referee"><div style={{fontWeight:600}}>{r.referee?.name}</div><div style={{color:C.muted,fontSize:11}}>{r.referee?.email}</div></Td>
                    <Td label="Flags">{flags.length===0?<span style={{color:C.muted}}>—</span>:<div style={{display:"flex",gap:4,flexWrap:"wrap"}}>{flags.map(f=><Badge key={f} label={f} color="red"/>)}</div>}</Td>
                    <Td label="Registered" style={{color:C.muted}}>{fmt(r.registeredAt)}</Td>
                    <Td label="Status"><Badge label={r.status.replace(/_/g," ")} color={statusColor(r.status)}/></Td>
                    <Td>
                      {r.status==="flagged"&&(
                        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                          <Btn variant="smSuccess" disabled={acting===r.id+"approve"} onClick={()=>doAction(r.id,"approve")}>Approve</Btn>
                          <Btn variant="smDanger" disabled={acting===r.id+"reject"} onClick={()=>doAction(r.id,"reject")}>Reject</Btn>
                        </div>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody></table>
          </TableWrap>
          <Pagination page={page} total={total} limit={50} onPage={setPage}/>
        </>
      )}
    </div>
  );
}

// ── Tickets ────────────────────────────────────────────────────────────────────
// Sub-tab: Participant tickets (existing behaviour)
function ParticipantTicketsTab() {
  const [rows,setRows]=useState([]); const [total,setTotal]=useState(0);
  const [statusFilter,setStatusFilter]=useState("open"); const [page,setPage]=useState(1); const [loading,setLoading]=useState(true);
  const [expanded,setExpanded]=useState(null); const [note,setNote]=useState(""); const [acting,setActing]=useState(false); const [msg,setMsg]=useState("");

  const load=useCallback(()=>{
    setLoading(true);
    const s=statusFilter==="all"?"":statusFilter;
    api("GET",`/tickets?status=${s}&page=${page}&limit=50`)
      .then(d=>{setRows(d.tickets||[]);setTotal(d.total||0);}).catch(()=>{}).finally(()=>setLoading(false));
  },[statusFilter,page]);
  useEffect(()=>{load();},[load]);

  async function updateTicket(id,status,resolutionNote) {
    setActing(true);
    try { await api("PATCH",`/tickets/${id}`,{...(status?{status}:{}),...(resolutionNote?{resolutionNote}:{})}); setMsg("Ticket updated"); setExpanded(null); setNote(""); load(); setTimeout(()=>setMsg(""),2500); }
    catch(e){ setMsg(e.message); } finally { setActing(false); }
  }

  const priorityColor=p=>({low:"grey",normal:"blue",high:"yellow",urgent:"red"}[p]||"grey");
  const catColor=c=>({payment:"red",technical:"blue",survey_content:"purple",other:"grey"}[c]||"grey");
  const statusColor=s=>({open:"yellow",in_review:"blue",resolved:"green"}[s]||"grey");

  return (
    <div>
      {msg&&<div style={{marginBottom:12,color:C.success,fontSize:13}}>{msg}</div>}
      <FilterTabs tabs={[{value:"open",label:"Open"},{value:"in_review",label:"In Review"},{value:"resolved",label:"Resolved"},{value:"all",label:"All"}]} active={statusFilter} onChange={v=>{setStatusFilter(v);setPage(1);}}/>
      {loading?<Spinner/>:(
        <>
          <TableWrap>
            <table className="rc-table"><thead className="rc-thead"><tr className="rc-tr"><Th>Participant</Th><Th>Category</Th><Th>Priority</Th><Th>Subject</Th><Th>Status</Th><Th>Created</Th><Th></Th></tr></thead>
            <tbody className="rc-tbody">
              {rows.length===0&&<tr className="rc-tr"><td colSpan={7}><EmptyState message="No tickets found"/></td></tr>}
              {rows.map(t=>(
                <React.Fragment key={t.id}>
                  <tr className="rc-tr" onClick={()=>setExpanded(expanded===t.id?null:t.id)} style={{cursor:"pointer"}}>
                    <Td label="Participant"><div style={{fontWeight:600}}>{t.participant?.name}</div><div style={{color:C.muted,fontSize:11}}>{t.participant?.email}</div></Td>
                    <Td label="Category"><Badge label={t.category.replace("_"," ")} color={catColor(t.category)}/></Td>
                    <Td label="Priority"><Badge label={t.priority} color={priorityColor(t.priority)}/></Td>
                    <Td label="Subject"><span style={{fontWeight:500}}>{t.subject}</span>{t.surveyName&&<div style={{color:C.muted,fontSize:11}}>{t.surveyName}</div>}</Td>
                    <Td label="Status"><Badge label={t.status.replace("_"," ")} color={statusColor(t.status)}/></Td>
                    <Td label="Created" style={{color:C.muted}}>{fmt(t.createdAt)}</Td>
                    <Td><Btn variant="smGhost" onClick={e=>{e.stopPropagation();setExpanded(expanded===t.id?null:t.id);}}>{expanded===t.id?"Close":"View"}</Btn></Td>
                  </tr>
                  {expanded===t.id&&(
                    <tr className="rc-tr" style={{background:C.surface,cursor:"default"}} onClick={e=>e.stopPropagation()}>
                      <td colSpan={7} style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`,display:"block"}}>
                        <div style={{marginBottom:10,fontSize:13,color:C.text}}><b>Description:</b><div style={{marginTop:6,color:C.muted,lineHeight:1.6}}>{t.description}</div></div>
                        {t.resolutionNote&&<div style={{marginBottom:10,fontSize:13}}><b>Resolution note:</b><div style={{marginTop:4,color:C.muted}}>{t.resolutionNote}</div></div>}
                        <textarea value={note} onChange={e=>setNote(e.target.value)} placeholder="Add resolution note…" rows={2} style={{width:"100%",padding:"8px 10px",background:C.card,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box",resize:"vertical",marginBottom:10}}/>
                        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                          {t.status==="open"&&<Btn variant="sm" onClick={()=>updateTicket(t.id,"in_review",note)}>Mark In Review</Btn>}
                          {t.status!=="resolved"&&<Btn variant="success" disabled={acting} onClick={()=>updateTicket(t.id,"resolved",note)}>Mark Resolved</Btn>}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody></table>
          </TableWrap>
          <Pagination page={page} total={total} limit={50} onPage={setPage}/>
        </>
      )}
    </div>
  );
}

function ConsoleFileIcon({ mimeType }) {
  if (mimeType && mimeType.startsWith("image/")) {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="2" width="20" height="20" rx="3" fill="rgba(59,130,246,0.15)" stroke="#3B82F6" strokeWidth="1.5"/>
        <circle cx="8" cy="9" r="1.8" fill="#3B82F6"/>
        <path d="M2 18l5.5-5.5 3.5 3.5 3-3 8 8" stroke="#3B82F6" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"/>
      </svg>
    );
  }
  if (mimeType === "application/pdf") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="2" width="20" height="20" rx="3" fill="rgba(239,68,68,0.15)" stroke="#EF4444" strokeWidth="1.5"/>
        <text x="12" y="15.5" textAnchor="middle" fontSize="7.5" fontWeight="800" fill="#EF4444" fontFamily="system-ui,sans-serif">PDF</text>
      </svg>
    );
  }
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <rect x="2" y="2" width="20" height="20" rx="3" fill="rgba(100,116,139,0.15)" stroke="#64748B" strokeWidth="1.5"/>
      <path d="M7 9h10M7 13h7" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function AdminAttachmentList({ attachments, ticketId }) {
  if (!attachments || attachments.length === 0) return null;
  const fmtSize = b => b < 1024*1024 ? `${(b/1024).toFixed(0)} KB` : `${(b/(1024*1024)).toFixed(1)} MB`;
  const truncate = (s, n) => s.length > n ? s.slice(0, n-1) + "…" : s;
  return (
    <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:8 }}>
      {attachments.map(a => {
        const url = `/api/admin/customer-tickets/${ticketId}/attachments/${a.id}`;
        return (
          <a key={a.id} href={url} target="_blank" rel="noopener noreferrer"
            style={{ display:"flex", alignItems:"center", gap:7, background:C.surface, border:`1px solid ${C.border}`, borderRadius:7, padding:"5px 10px", textDecoration:"none", maxWidth:200 }}>
            <ConsoleFileIcon mimeType={a.mimeType}/>
            <div style={{minWidth:0}}>
              <div style={{fontSize:12,color:C.textDim||"#CBD5E1",fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{truncate(a.originalName,22)}</div>
              <div style={{fontSize:10,color:C.muted}}>{fmtSize(a.sizeBytes)}</div>
            </div>
          </a>
        );
      })}
    </div>
  );
}

function AdminFileUpload({ ticketId, onUploaded }) {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      await fetch(`/api/admin/customer-tickets/${ticketId}/attachments`, {
        method: "POST",
        credentials: "include",
        body: fd,
      }).catch(() => {});
    }
    setUploading(false);
    e.target.value = "";
    onUploaded && onUploaded();
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
      <input ref={inputRef} type="file" multiple accept="image/*,application/pdf" style={{ display: "none" }} onChange={handleFiles} />
      <button onClick={() => inputRef.current?.click()} disabled={uploading}
        style={{ fontSize: 12, color: C.muted, background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontFamily: "inherit" }}>
        {uploading ? "Uploading…" : "📎 Attach file"}
      </button>
    </div>
  );
}

// Sub-tab: Customer tickets
function CustomerTicketsTab() {
  const [rows,setRows]=useState([]); const [total,setTotal]=useState(0);
  const [statusFilter,setStatusFilter]=useState("open"); const [typeFilter,setTypeFilter]=useState("all");
  const [search,setSearch]=useState(""); const [page,setPage]=useState(1); const [loading,setLoading]=useState(true);
  const [expanded,setExpanded]=useState(null); const [expandedTicket,setExpandedTicket]=useState(null);
  const [note,setNote]=useState(""); const [reply,setReply]=useState(""); const [replyFiles,setReplyFiles]=useState([]); const [fileError,setFileError]=useState("");
  const [acting,setActing]=useState(false); const [msg,setMsg]=useState("");

  const load=useCallback(()=>{
    setLoading(true);
    const p=new URLSearchParams({page,limit:50});
    if(statusFilter!=="all") p.set("status",statusFilter);
    if(typeFilter!=="all")   p.set("type",typeFilter);
    if(search.trim())        p.set("search",search.trim());
    api("GET",`/customer-tickets?${p}`)
      .then(d=>{setRows(d.tickets||[]);setTotal(d.total||0);}).catch(()=>{}).finally(()=>setLoading(false));
  },[statusFilter,typeFilter,search,page]);
  useEffect(()=>{load();},[load]);

  async function loadThread(id) {
    if(expanded===id){setExpanded(null);setExpandedTicket(null);return;}
    setExpanded(id);
    try{const d=await api("GET",`/customer-tickets/${id}`);setExpandedTicket(d.ticket);}catch{}
  }

  async function updateTicket(id,patch) {
    setActing(true);
    try{ await api("PATCH",`/customer-tickets/${id}`,patch); setMsg("Ticket updated"); load(); setTimeout(()=>setMsg(""),2500); }
    catch(e){setMsg(e.message);} finally{setActing(false);}
  }

  async function sendReply(id) {
    if(!reply.trim() && replyFiles.length===0) return;
    setActing(true);
    try{
      let commentId=null;
      if(reply.trim()){
        const r=await api("POST",`/customer-tickets/${id}/comments`,{content:reply.trim()});
        commentId=r.comment?.id||null;
      }
      if(replyFiles.length>0){
        for(const file of replyFiles){
          const fd=new FormData(); fd.append("file",file);
          if(commentId) fd.append("commentId",commentId);
          const ur=await fetch(`/api/admin/customer-tickets/${id}/attachments`,{method:"POST",credentials:"include",body:fd});
        if(!ur.ok){const e=await ur.json().catch(()=>({}));throw new Error(e.error||`Upload failed (${ur.status})`);}

        }
        setReplyFiles([]);
      }
      setReply("");
      if(note.trim()) await api("PATCH",`/customer-tickets/${id}`,{resolutionNote:note.trim()});
      const d=await api("GET",`/customer-tickets/${id}`);
      setExpandedTicket(d.ticket);
      setMsg("Reply sent"); setTimeout(()=>setMsg(""),2500);
    } catch(e){setMsg(e.message);} finally{setActing(false);}
  }

  const priorityColor=p=>({low:"grey",normal:"blue",high:"yellow",urgent:"red"}[p]||"grey");
  const statusColor=s=>({open:"yellow",in_review:"blue",resolved:"green",closed:"grey"}[s]||"grey");
  const typeColor=t=>t==="issue"?"red":"blue";
  const catLabel=c=>c.replace(/_/g," ");
  const fmtDt=d=>d?new Date(d).toLocaleString("en-GB",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}):"—";

  return (
    <div>
      {msg&&<div style={{marginBottom:12,color:C.success,fontSize:13}}>{msg}</div>}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12,alignItems:"center"}}>
        <FilterTabs tabs={[{value:"open",label:"Open"},{value:"in_review",label:"In Review"},{value:"resolved",label:"Resolved"},{value:"closed",label:"Closed"},{value:"all",label:"All"}]} active={statusFilter} onChange={v=>{setStatusFilter(v);setPage(1);}}/>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16,alignItems:"center"}}>
        <FilterTabs tabs={[{value:"all",label:"All Types"},{value:"issue",label:"Issues"},{value:"feedback",label:"Feedback"}]} active={typeFilter} onChange={v=>{setTypeFilter(v);setPage(1);}}/>
        <SearchBar value={search} onChange={v=>{setSearch(v);setPage(1);}} placeholder="Search company, subject…"/>
      </div>
      {loading?<Spinner/>:(
        <>
          <TableWrap>
            <table className="rc-table">
              <thead className="rc-thead"><tr className="rc-tr">
                <Th>Company</Th><Th>Filed By</Th><Th>Type</Th><Th>Category</Th><Th>Subject</Th><Th>Priority</Th><Th>Status</Th><Th>Created</Th><Th></Th>
              </tr></thead>
              <tbody className="rc-tbody">
                {rows.length===0&&<tr className="rc-tr"><td colSpan={9}><EmptyState message="No customer tickets found"/></td></tr>}
                {rows.map(t=>(
                  <React.Fragment key={t.id}>
                    <tr className="rc-tr" onClick={()=>loadThread(t.id)} style={{cursor:"pointer"}}>
                      <Td label="Company"><div style={{fontWeight:600}}>{t.customer?.companyName||"—"}</div></Td>
                      <Td label="Filed By"><div style={{fontSize:12}}>{t.portalUser?`${t.portalUser.firstName} ${t.portalUser.lastName}`.trim():"—"}</div><div style={{fontSize:11,color:C.muted}}>{t.portalUser?.email||""}</div></Td>
                      <Td label="Type"><Badge label={t.type} color={typeColor(t.type)}/></Td>
                      <Td label="Category"><Badge label={catLabel(t.category)} color="grey"/></Td>
                      <Td label="Subject"><span style={{fontWeight:500}}>{t.subject}</span>{t.survey&&<div style={{color:C.muted,fontSize:11}}>{t.survey.title}</div>}</Td>
                      <Td label="Priority"><Badge label={t.priority} color={priorityColor(t.priority)}/></Td>
                      <Td label="Status"><Badge label={t.status.replace("_"," ")} color={statusColor(t.status)}/></Td>
                      <Td label="Created" style={{color:C.muted}}>{fmt(t.createdAt)}</Td>
                      <Td><Btn variant="smGhost" onClick={e=>{e.stopPropagation();loadThread(t.id);}}>{expanded===t.id?"Close":"View"}</Btn></Td>
                    </tr>
                    {expanded===t.id&&(
                      <tr className="rc-tr" style={{background:C.surface,cursor:"default"}} onClick={e=>e.stopPropagation()}>
                        <td colSpan={9} style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`}}>
                          {/* Description */}
                          <div style={{marginBottom:14,fontSize:13}}>
                            <b>Description</b>
                            <div style={{marginTop:6,color:C.muted,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{expandedTicket?.description||t.description}</div>
                            <AdminAttachmentList attachments={(expandedTicket?.attachments||[]).filter(a=>!a.commentId)} ticketId={t.id}/>
                          </div>

                          {/* Comment thread */}
                          {(expandedTicket?.comments||[]).length>0&&(
                            <div style={{marginBottom:14}}>
                              <b style={{fontSize:13}}>Thread</b>
                              <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:8}}>
                                {(expandedTicket.comments||[]).map(c=>{
                                  const isAdmin=c.authorType==="admin";
                                  const cAtts=(expandedTicket.attachments||[]).filter(a=>a.commentId===c.id);
                                  return(
                                    <div key={c.id} style={{padding:"10px 14px",borderRadius:8,fontSize:13,
                                      background:isAdmin?"rgba(79,110,247,0.08)":C.card,
                                      border:`1px solid ${isAdmin?C.info+"44":C.border}`}}>
                                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                                        <span style={{fontWeight:600,fontSize:12,color:isAdmin?C.info:C.text}}>{isAdmin?"Rik.ai Support":c.authorName}</span>
                                        <span style={{fontSize:11,color:C.muted}}>{fmtDt(c.createdAt)}</span>
                                      </div>
                                      <div style={{color:C.muted,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{c.content}</div>
                                      {cAtts.length>0&&<AdminAttachmentList attachments={cAtts} ticketId={t.id}/>}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Resolution note display */}
                          {expandedTicket?.resolutionNote&&(
                            <div style={{marginBottom:14,padding:"10px 14px",borderRadius:8,background:"rgba(34,197,94,0.08)",border:`1px solid ${C.success}44`,fontSize:13}}>
                              <b style={{color:C.success}}>Resolution Note</b>
                              <div style={{marginTop:4,color:C.muted}}>{expandedTicket.resolutionNote}</div>
                            </div>
                          )}

                          {/* Admin reply */}
                          {(expandedTicket?.status||t.status)!=="closed"&&(
                            <div style={{marginBottom:10}}>
                              <div style={{fontSize:13,fontWeight:600,marginBottom:6}}>Reply to customer</div>
                              <textarea value={reply} onChange={e=>setReply(e.target.value)} placeholder="Type your reply…" rows={3}
                                style={{width:"100%",padding:"8px 10px",background:C.card,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box",resize:"vertical",marginBottom:8}}/>
                              <div style={{marginBottom:8}}>
                                <input type="file" multiple accept="image/jpeg,image/png,image/gif,image/webp,application/pdf" style={{fontSize:12,color:C.muted}} onChange={e=>{
                                  const ALLOWED=["image/jpeg","image/png","image/gif","image/webp","application/pdf"];
                                  const rejected=[]; setFileError("");
                                  const valid=Array.from(e.target.files||[]).filter(f=>{
                                    if(!ALLOWED.includes(f.type)){rejected.push(`"${f.name}"`);return false;}
                                    if(f.size>5*1024*1024){rejected.push(`"${f.name}" (>5 MB)`);return false;}
                                    return true;
                                  });
                                  if(rejected.length) setFileError(`Rejected: ${rejected.join(", ")} — only JPEG, PNG, GIF, WebP, PDF allowed (max 5 MB).`);
                                  setReplyFiles(p=>[...p,...valid].slice(0,20));
                                  e.target.value="";
                                }}/>
                                {fileError&&<div style={{fontSize:11,color:C.danger,marginTop:4}}>{fileError}</div>}
                                {replyFiles.length>0&&<div style={{marginTop:4,display:"flex",flexWrap:"wrap",gap:6}}>{replyFiles.map((f,i)=>(
                                  <span key={i} style={{fontSize:11,background:C.surface,border:`1px solid ${C.border}`,borderRadius:4,padding:"2px 8px",color:C.muted,display:"flex",gap:4,alignItems:"center"}}>
                                    {f.name}<button onClick={()=>setReplyFiles(p=>p.filter((_,j)=>j!==i))} style={{background:"none",border:"none",color:C.danger,cursor:"pointer",padding:0,lineHeight:1}}>×</button>
                                  </span>
                                ))}</div>}
                              </div>
                              <textarea value={note} onChange={e=>setNote(e.target.value)} placeholder="Internal resolution note (optional, visible to customer)…" rows={2}
                                style={{width:"100%",padding:"8px 10px",background:C.card,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,fontSize:13,fontFamily:"inherit",boxSizing:"border-box",resize:"vertical",marginTop:8,marginBottom:10}}/>
                            </div>
                          )}

                          {/* Action buttons */}
                          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                            {(reply.trim()||replyFiles.length>0)&&<Btn variant="primary" disabled={acting} onClick={()=>sendReply(t.id)}>Send Reply</Btn>}
                            {(expandedTicket?.status||t.status)==="open"&&<Btn variant="sm" disabled={acting} onClick={()=>{updateTicket(t.id,{status:"in_review",...(note.trim()?{resolutionNote:note}:{})});setNote("");}}>Mark In Review</Btn>}
                            {["open","in_review"].includes(expandedTicket?.status||t.status)&&<Btn variant="success" disabled={acting} onClick={()=>{updateTicket(t.id,{status:"resolved",...(note.trim()?{resolutionNote:note}:{})});setNote("");setExpanded(null);}}>Mark Resolved</Btn>}
                            {(expandedTicket?.status||t.status)!=="closed"&&<Btn variant="smGhost" disabled={acting} onClick={()=>{updateTicket(t.id,{status:"closed"});setExpanded(null);}}>Close Ticket</Btn>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </TableWrap>
          <Pagination page={page} total={total} limit={50} onPage={setPage}/>
        </>
      )}
    </div>
  );
}

// Wrapper with top-level sub-tabs
function TicketsScreen() {
  const [tab,setTab]=useState("customer");
  return (
    <div>
      <div style={{display:"flex",gap:4,marginBottom:20,borderBottom:`1px solid ${C.border}`,paddingBottom:0}}>
        {[{value:"customer",label:"Customer Tickets"},{value:"participant",label:"Participant Tickets"}].map(t=>(
          <button key={t.value} onClick={()=>setTab(t.value)} style={{
            padding:"8px 18px",fontSize:13,fontWeight:600,border:"none",cursor:"pointer",fontFamily:"inherit",
            background:"transparent",color:tab===t.value?C.accent:C.muted,
            borderBottom:tab===t.value?`2px solid ${C.accent}`:"2px solid transparent",
            marginBottom:-1,
          }}>{t.label}</button>
        ))}
      </div>
      {tab==="participant"?<ParticipantTicketsTab/>:<CustomerTicketsTab/>}
    </div>
  );
}

// ── Suppliers Screen ──────────────────────────────────────────────────────────
function SuppliersScreen() {
  const [suppliers,        setSuppliers]        = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [creating,         setCreating]         = useState(false);
  const [newName,          setNewName]          = useState("");
  const [newCallback,      setNewCallback]      = useState("");
  const [newEmail,         setNewEmail]         = useState("");
  const [newSecret,        setNewSecret]        = useState(null);
  const [error,            setError]            = useState(null);
  const [copiedKey,        setCopiedKey]        = useState(null);
  const [surveys,          setSurveys]          = useState([]);
  const [allocSurvey,      setAllocSurvey]      = useState("");
  const [allocTarget,      setAllocTarget]      = useState("");
  const [allocCap,         setAllocCap]         = useState("");
  const [allocSupp,        setAllocSupp]        = useState(null);
  // Credentials reveal
  const [visibleKeys,      setVisibleKeys]      = useState({});  // { id: bool }
  const [revealedSecrets,  setRevealedSecrets]  = useState({});  // { id: { visible, value, loading } }
  // Suspend confirm modal
  const [suspendTarget,    setSuspendTarget]    = useState(null);
  // Survey history per supplier card
  const [expandedSupplier, setExpandedSupplier] = useState(null);
  const [supplierSurveys,  setSupplierSurveys]  = useState({});  // { id: { loading, surveys[] } }
  // Reconciliation (scoped to supplier × survey)
  const [reconContext,     setReconContext]      = useState(null); // { supplierId, supplierName, surveyId, surveyTitle }
  const [recon,            setRecon]            = useState(null);
  const [expandedPids,     setExpandedPids]      = useState({});  // { externalParticipantId: bool }
  const [csvDownloading,   setCsvDownloading]    = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api("GET", "/suppliers");
      setSuppliers(d.suppliers || []);
    } catch { setError("Failed to load suppliers."); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const d = await api("POST", "/suppliers", { name: newName.trim(), callbackUrl: newCallback || undefined, contactEmail: newEmail || undefined });
      setNewSecret({ secret: d.secret, apiKey: d.supplier.apiKey, name: d.supplier.name });
      setNewName(""); setNewCallback(""); setNewEmail("");
      load();
    } catch { setError("Failed to create supplier."); }
    finally  { setCreating(false); }
  };

  const confirmToggle = async (s) => {
    setSuspendTarget(null);
    try {
      await api("PATCH", `/suppliers/${s.id}`, { status: s.status === "active" ? "suspended" : "active" });
      load();
    } catch { setError("Failed to update supplier status."); }
  };

  const openAllocate = async (s) => {
    setAllocSupp(s); setAllocSurvey(""); setAllocTarget(""); setAllocCap("");
    try {
      const r = await fetch("/api/surveys", { credentials: "include" });
      const d = await r.json();
      setSurveys((d.surveys || []).filter(sv => sv.status === "active"));
    } catch {}
  };

  const saveAlloc = async () => {
    if (!allocSurvey || !allocSupp) return;
    try {
      await api("POST", `/surveys/${allocSurvey}/suppliers`, { supplierId: allocSupp.id, softTarget: allocTarget ? parseInt(allocTarget) : undefined, hardCap: allocCap ? parseInt(allocCap) : undefined });
      setAllocSupp(null);
      // Invalidate survey history cache for this supplier so it re-fetches on next expand
      setSupplierSurveys(prev => { const n = { ...prev }; delete n[allocSupp.id]; return n; });
    } catch { setError("Failed to save allocation."); }
  };

  const toggleSupplierSurveys = async (s) => {
    if (expandedSupplier === s.id) { setExpandedSupplier(null); return; }
    setExpandedSupplier(s.id);
    if (supplierSurveys[s.id] && !supplierSurveys[s.id].loading) return; // already loaded
    setSupplierSurveys(prev => ({ ...prev, [s.id]: { loading: true, surveys: [] } }));
    try {
      const d = await api("GET", `/suppliers/${s.id}/surveys`);
      setSupplierSurveys(prev => ({ ...prev, [s.id]: { loading: false, surveys: d.surveys || [] } }));
    } catch {
      setSupplierSurveys(prev => ({ ...prev, [s.id]: { loading: false, surveys: [] } }));
    }
  };

  const openRecon = async (supplierId, supplierName, surveyId, surveyTitle) => {
    setReconContext({ supplierId, supplierName, surveyId, surveyTitle });
    setRecon(null);
    setExpandedPids({});
    try {
      const d = await api("GET", `/surveys/${surveyId}/reconciliation?supplierId=${supplierId}`);
      setRecon(d);
    } catch { setError("Failed to load reconciliation."); }
  };

  const revealSecret = async (s) => {
    const cur = revealedSecrets[s.id];
    if (cur?.visible) { setRevealedSecrets(prev => ({ ...prev, [s.id]: { ...prev[s.id], visible: false } })); return; }
    if (cur?.value)   { setRevealedSecrets(prev => ({ ...prev, [s.id]: { ...prev[s.id], visible: true  } })); return; }
    setRevealedSecrets(prev => ({ ...prev, [s.id]: { loading: true, visible: false, value: null } }));
    try {
      const d = await api("GET", `/suppliers/${s.id}/secret`);
      setRevealedSecrets(prev => ({ ...prev, [s.id]: { loading: false, visible: true, value: d.secret } }));
    } catch {
      setRevealedSecrets(prev => ({ ...prev, [s.id]: { loading: false, visible: false, value: null } }));
      setError("Failed to reveal secret.");
    }
  };

  const rotateSecret = async (s) => {
    if (!window.confirm(`Rotate HMAC secret for ${s.name}?\n\nThe supplier must update their integration immediately. Old secret stops working at once.`)) return;
    try {
      const d = await api("POST", `/suppliers/${s.id}/rotate-secret`);
      setRevealedSecrets(prev => ({ ...prev, [s.id]: { loading: false, visible: true, value: d.secret } }));
    } catch { setError("Failed to rotate secret."); }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => { setCopiedKey(key); setTimeout(() => setCopiedKey(null), 2000); });
  };

  const downloadReconCsv = async () => {
    if (!reconContext) return;
    setCsvDownloading(true);
    try {
      const res = await fetch(`/api/admin/surveys/${reconContext.surveyId}/reconciliation/export?supplierId=${reconContext.supplierId}`, { credentials: "include" });
      if (res.status === 401) { window.__adminLogout?.(); return; }
      if (!res.ok) { setError("Failed to export reconciliation CSV."); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url;
      a.download = `reconciliation-${reconContext.surveyId}-${reconContext.supplierId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("Failed to export reconciliation CSV.");
    } finally {
      setCsvDownloading(false);
    }
  };

  const maskCred = (val) => {
    if (!val) return "—";
    if (val.length <= 8) return "●".repeat(val.length);
    return val.slice(0, 6) + "●".repeat(12) + val.slice(-4);
  };

  const box    = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "18px 20px", marginBottom: 16 };
  const inp    = { width: "100%", background: "#111827", border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 12px", color: C.text, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };
  const btn    = (extra={}) => ({ padding: "8px 16px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 13, fontFamily: "inherit", fontWeight: 600, ...extra });
  const iconBtn = { background: "none", border: "none", cursor: "pointer", padding: "2px 6px", borderRadius: 4, fontSize: 14, color: C.muted, fontFamily: "inherit" };

  if (loading) return <div style={{ padding: 32, color: C.muted }}>Loading suppliers…</div>;

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "24px 16px" }}>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 20 }}>Suppliers</div>
      {error && <div style={{ background: "#3B1818", border: "1px solid #7F1D1D", borderRadius: 8, padding: "10px 16px", color: "#FCA5A5", marginBottom: 16, fontSize: 13, display: "flex", justifyContent: "space-between" }}><span>{error}</span><button onClick={()=>setError(null)} style={{ background:"none",border:"none",color:"#FCA5A5",cursor:"pointer",fontFamily:"inherit" }}>✕</button></div>}

      {/* One-time secret reveal after creation */}
      {newSecret && (
        <div style={{ ...box, background: "#0C2A1A", borderColor: "#065F46" }}>
          <div style={{ fontWeight: 700, marginBottom: 6, color: "#6EE7B7" }}>Supplier created — save these credentials now</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>The secret will not be shown again. Store it securely and share with the supplier via a secure channel.</div>
          {[
            ["Supplier name",  newSecret.name,   "#F1F5F9"],
            ["API Key",        newSecret.apiKey,  "#A7F3D0"],
            ["HMAC Secret",    newSecret.secret,  "#FDE68A"],
          ].map(([label, val, clr]) => (
            <div key={label} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>{label}</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <code style={{ flex: 1, background: "#111827", padding: "7px 10px", borderRadius: 5, fontSize: 12, color: clr, wordBreak: "break-all", fontFamily: "monospace" }}>{val}</code>
                <button onClick={() => copy(val, "new_" + label)} style={btn({ background: C.accentDim, color: C.accent, fontSize: 12 })}>{copiedKey === "new_" + label ? "✓ Copied" : "Copy"}</button>
              </div>
            </div>
          ))}
          <button onClick={() => setNewSecret(null)} style={btn({ background: C.border, color: C.muted, marginTop: 8 })}>Dismiss</button>
        </div>
      )}

      {/* Register new supplier */}
      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: 14, fontSize: 14 }}>Register new supplier</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
          <div><div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Supplier name *</div><input style={inp} value={newName} onChange={e=>setNewName(e.target.value)} placeholder="Panel Focus" /></div>
          <div><div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>S2S callback URL</div><input style={inp} value={newCallback} onChange={e=>setNewCallback(e.target.value)} placeholder="https://supplier.com/callback" /></div>
          <div><div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Contact email</div><input style={inp} value={newEmail} onChange={e=>setNewEmail(e.target.value)} placeholder="pm@supplier.com" /></div>
        </div>
        <button onClick={create} disabled={creating || !newName.trim()} style={btn({ background: C.accent, color: "#fff", opacity: (!newName.trim() || creating) ? 0.5 : 1 })}>{creating ? "Creating…" : "Register supplier"}</button>
      </div>

      {/* Supplier list */}
      {suppliers.length === 0 && <div style={{ color: C.muted, fontSize: 13 }}>No suppliers registered yet.</div>}
      {suppliers.map(s => {
        const secState = revealedSecrets[s.id] || {};
        const keyVisible = !!visibleKeys[s.id];
        const isExpanded = expandedSupplier === s.id;
        const surveyData = supplierSurveys[s.id];
        return (
          <div key={s.id} style={box}>
            {/* Header row */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{s.name}</div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>{s.contactEmail || "—"}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{s.callbackUrl || "No callback URL"}</div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 99, background: s.status === "active" ? "#065F46" : "#3B1818", color: s.status === "active" ? "#6EE7B7" : "#FCA5A5", fontWeight: 600 }}>{s.status}</span>
                <button onClick={() => openAllocate(s)} style={btn({ background: "#1F2937", color: C.text, fontSize: 12 })}>Assign to survey</button>
                <button onClick={() => setSuspendTarget(s)} style={btn({ background: s.status === "active" ? "#3B1818" : "#052e16", color: s.status === "active" ? "#FCA5A5" : "#6EE7B7", fontSize: 12 })}>
                  {s.status === "active" ? "Suspend" : "Activate"}
                </button>
              </div>
            </div>

            {/* Credential rows */}
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
              {/* API Key */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#111827", borderRadius: 6, padding: "6px 10px" }}>
                <span style={{ fontSize: 11, color: C.muted, minWidth: 72, flexShrink: 0 }}>API Key</span>
                <code style={{ flex: 1, fontSize: 12, color: "#A7F3D0", fontFamily: "monospace", letterSpacing: "0.02em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {keyVisible ? s.apiKey : maskCred(s.apiKey)}
                </code>
                <button title={keyVisible ? "Hide" : "Show"} onClick={() => setVisibleKeys(prev => ({ ...prev, [s.id]: !prev[s.id] }))} style={iconBtn}>{keyVisible ? "🙈" : "👁"}</button>
                <button title="Copy API Key" onClick={() => copy(s.apiKey, s.id + "_key")} style={{ ...iconBtn, color: copiedKey === s.id + "_key" ? "#6EE7B7" : C.muted }}>{copiedKey === s.id + "_key" ? "✓" : "📋"}</button>
              </div>
              {/* HMAC Secret */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#111827", borderRadius: 6, padding: "6px 10px" }}>
                <span style={{ fontSize: 11, color: C.muted, minWidth: 72, flexShrink: 0 }}>HMAC Secret</span>
                <code style={{ flex: 1, fontSize: 12, color: "#FDE68A", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {secState.loading ? "loading…" : secState.visible && secState.value ? secState.value : "●●●●●●●●●●●●●●●●●●●●"}
                </code>
                <button title={secState.visible ? "Hide" : "Reveal"} onClick={() => revealSecret(s)} style={iconBtn}>{secState.loading ? "⟳" : secState.visible ? "🙈" : "👁"}</button>
                {secState.visible && secState.value && (
                  <button title="Copy secret" onClick={() => copy(secState.value, s.id + "_secret")} style={{ ...iconBtn, color: copiedKey === s.id + "_secret" ? "#FDE68A" : C.muted }}>{copiedKey === s.id + "_secret" ? "✓" : "📋"}</button>
                )}
                <button title="Rotate secret" onClick={() => rotateSecret(s)} style={{ ...iconBtn, fontSize: 15, color: C.accent }}>↻</button>
              </div>
            </div>

            {/* Stats + survey history toggle */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
              <span style={{ fontSize: 11, color: C.muted }}>{s._count?.sessions ?? 0} sessions · {s._count?.allocations ?? 0} survey{s._count?.allocations !== 1 ? "s" : ""}</span>
              <button onClick={() => toggleSupplierSurveys(s)} style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", fontFamily: "inherit", padding: 0, display: "flex", alignItems: "center", gap: 4 }}>
                {isExpanded ? "▲ Hide surveys" : "▼ Survey history"}
              </button>
            </div>

            {/* Survey history table */}
            {isExpanded && (
              <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 10, paddingTop: 12 }}>
                {surveyData?.loading ? (
                  <div style={{ color: C.muted, fontSize: 12 }}>Loading…</div>
                ) : !surveyData || surveyData.surveys.length === 0 ? (
                  <div style={{ color: C.muted, fontSize: 12 }}>No surveys assigned yet. Use "Assign to survey" above.</div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr style={{ color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                          {["Survey","Status","Completions","Target / Cap","Pass","Fail","Review","Acceptance","Avg Engagement","Assigned",""].map(h => (
                            <th key={h} style={{ padding: "5px 8px", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {surveyData.surveys.map(sv => (
                          <tr key={sv.surveyId} style={{ borderBottom: `1px solid ${C.border}` }}>
                            <td style={{ padding: "6px 8px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={sv.surveyTitle}>{sv.surveyTitle}</td>
                            <td style={{ padding: "6px 8px" }}>
                              <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 99, fontWeight: 600, background: sv.surveyStatus === "active" ? "#065F46" : "#1F2937", color: sv.surveyStatus === "active" ? "#6EE7B7" : C.muted }}>{sv.surveyStatus}</span>
                            </td>
                            <td style={{ padding: "6px 8px" }}>{sv.completions}</td>
                            <td style={{ padding: "6px 8px", color: C.muted }}>{sv.softTarget ?? "—"} / {sv.hardCap ?? "∞"}</td>
                            <td style={{ padding: "6px 8px", color: "#6EE7B7" }}>{sv.pass}</td>
                            <td style={{ padding: "6px 8px", color: "#FCA5A5" }}>{sv.fail}</td>
                            <td style={{ padding: "6px 8px", color: "#FDE68A" }}>{sv.review}</td>
                            <td style={{ padding: "6px 8px" }}>
                              {sv.acceptanceRate != null
                                ? <span style={{ fontWeight: 600, color: sv.acceptanceRate >= 85 ? "#6EE7B7" : sv.acceptanceRate >= 60 ? "#FDE68A" : "#FCA5A5" }}>{sv.acceptanceRate}%</span>
                                : <span style={{ color: C.muted }}>—</span>}
                            </td>
                            <td style={{ padding: "6px 8px" }}>{sv.avgEngagement != null ? `${sv.avgEngagement}%` : "—"}</td>
                            <td style={{ padding: "6px 8px", color: C.muted }}>{new Date(sv.assignedAt).toLocaleDateString()}</td>
                            <td style={{ padding: "6px 8px" }}>
                              <button onClick={() => openRecon(s.id, s.name, sv.surveyId, sv.surveyTitle)} style={btn({ background: C.accentDim, color: C.accent, fontSize: 11, padding: "3px 10px" })}>Reconcile</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Assign supplier to survey modal */}
      {allocSupp && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }} onClick={()=>setAllocSupp(null)}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, width: 420, maxWidth: "95vw" }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontWeight: 700, marginBottom: 16 }}>Assign {allocSupp.name} to survey</div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Survey *</div>
              <select style={{ ...inp }} value={allocSurvey} onChange={e=>setAllocSurvey(e.target.value)}>
                <option value="">Select an active survey…</option>
                {surveys.map(sv => <option key={sv.id} value={sv.id}>{sv.title}</option>)}
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
              <div><div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Soft target (planning)</div><input style={inp} type="number" min="0" value={allocTarget} onChange={e=>setAllocTarget(e.target.value)} placeholder="e.g. 100" /></div>
              <div><div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Hard cap (max accepted)</div><input style={inp} type="number" min="0" value={allocCap} onChange={e=>setAllocCap(e.target.value)} placeholder="e.g. 200" /></div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={saveAlloc} disabled={!allocSurvey} style={btn({ background: C.accent, color: "#fff", opacity: !allocSurvey ? 0.5 : 1 })}>Save allocation</button>
              <button onClick={()=>setAllocSupp(null)} style={btn({ background: C.border, color: C.muted })}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend / Activate confirmation modal */}
      {suspendTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }} onClick={()=>setSuspendTarget(null)}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, width: 440, maxWidth: "95vw" }} onClick={e=>e.stopPropagation()}>
            {suspendTarget.status === "active" ? (
              <>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12, color: "#FCA5A5" }}>Suspend {suspendTarget.name}?</div>
                <p style={{ fontSize: 13, color: C.muted, marginBottom: 10, lineHeight: 1.65 }}>
                  New participants redirected from this supplier will be blocked immediately — the entry endpoint will return 403.
                </p>
                {suspendTarget.activeSessionCount > 0 && (
                  <div style={{ background: "#451A03", border: "1px solid #92400E", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#FDE68A", lineHeight: 1.6 }}>
                    ⚠️ {suspendTarget.activeSessionCount} participant{suspendTarget.activeSessionCount !== 1 ? "s are" : " is"} currently mid-interview and will not be interrupted. S2S postbacks for those sessions will still fire normally.
                  </div>
                )}
                <p style={{ fontSize: 12, color: C.muted, marginBottom: 20, lineHeight: 1.6 }}>
                  All previously attributed sessions and reconciliation data are preserved.
                </p>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => confirmToggle(suspendTarget)} style={btn({ background: C.danger, color: "#fff" })}>Confirm Suspend</button>
                  <button onClick={() => setSuspendTarget(null)} style={btn({ background: C.border, color: C.muted })}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12, color: "#6EE7B7" }}>Activate {suspendTarget.name}?</div>
                <p style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.65 }}>
                  Participants from this supplier will be able to enter allocated surveys again immediately.
                </p>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => confirmToggle(suspendTarget)} style={btn({ background: C.success, color: "#fff" })}>Confirm Activate</button>
                  <button onClick={() => setSuspendTarget(null)} style={btn({ background: C.border, color: C.muted })}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Reconciliation view — scoped to supplier × survey */}
      {reconContext && (
        <div style={{ ...box, marginTop: 24 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14, gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{reconContext.supplierName} — {reconContext.surveyTitle}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Reconciliation report</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={downloadReconCsv} disabled={csvDownloading} style={btn({ background: C.accentDim, color: C.accent, fontSize: 12, opacity: csvDownloading ? 0.6 : 1 })}>{csvDownloading ? "Downloading…" : "Download CSV"}</button>
              <button onClick={() => { setReconContext(null); setRecon(null); }} style={btn({ background: C.border, color: C.muted, fontSize: 12 })}>Close</button>
            </div>
          </div>
          {!recon ? <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div> : (() => {
            // Group sessions by external participant — one headline row per
            // participant, extra attempts collapse behind a toggle. recon.sessions
            // is ordered by createdAt desc (server-side), so the first session
            // seen per participant is their latest attempt: for reconciliation
            // the latest status holds good (unlike the Progress tab, which
            // surfaces the "best" attempt).
            const reconGroups = (() => {
              const m = new Map(); // pid key → session[] (insertion order = createdAt desc)
              (recon.sessions || []).forEach(r => {
                const key = r.externalParticipantId || r.sessionId;
                if (!m.has(key)) m.set(key, []);
                m.get(key).push(r);
              });
              return Array.from(m.entries()).map(([key, attempts]) => ({ key, headline: attempts[0], attempts }));
            })();

            const reconCells = (r) => (
              <>
                <td style={{ padding: "6px 10px" }}>{r.status}</td>
                <td style={{ padding: "6px 10px" }}>
                  <span style={{ padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 600,
                    background: r.qualityFlag === "pass" ? "#065F46" : r.qualityFlag === "fail" ? "#3B1818" : r.qualityFlag === "review" ? "#451A03" : "#1F2937",
                    color:      r.qualityFlag === "pass" ? "#6EE7B7" : r.qualityFlag === "fail" ? "#FCA5A5" : r.qualityFlag === "review" ? "#FDE68A" : C.muted,
                  }}>{r.qualityFlag || "pending"}</span>
                </td>
                <td style={{ padding: "6px 10px", color: C.muted, fontSize: 11 }}>{(r.reasonCodes || []).join(", ") || "—"}</td>
                <td style={{ padding: "6px 10px" }}>{r.durationSeconds ? `${Math.floor(r.durationSeconds/60)}m ${r.durationSeconds%60}s` : "—"}</td>
                <td style={{ padding: "6px 10px" }}>{r.wordCount ?? "—"}</td>
                <td style={{ padding: "6px 10px" }}>{r.engagementScore != null ? `${Math.round(r.engagementScore)}%` : "—"}</td>
                <td style={{ padding: "6px 10px" }}>
                  <span style={{ padding: "2px 8px", borderRadius: 99, fontSize: 11,
                    background: r.adminReviewStatus === "approved" ? "#065F46" : r.adminReviewStatus === "rejected" ? "#3B1818" : "#1F2937",
                    color:      r.adminReviewStatus === "approved" ? "#6EE7B7" : r.adminReviewStatus === "rejected" ? "#FCA5A5" : C.muted,
                  }}>{r.adminReviewStatus || "—"}</span>
                </td>
              </>
            );

            return (
              <>
                {/* Summary card — one entry per participant, not per attempt */}
                {(recon.summary || []).map(s => (
                  <div key={s.supplierId} style={{ display: "inline-flex", gap: 20, background: "#111827", borderRadius: 8, padding: "12px 18px", marginBottom: 16, flexWrap: "wrap" }}>
                    {[["Total", s.total, C.text], ["Pass", s.pass, "#6EE7B7"], ["Fail", s.fail, "#FCA5A5"], ["Review", s.review, "#FDE68A"], ["Pending QC", s.pending, C.muted]].map(([l, v, clr]) => (
                      <div key={l} style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: clr }}>{v}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{l}</div>
                      </div>
                    ))}
                    <div style={{ borderLeft: `1px solid ${C.border}`, paddingLeft: 20, textAlign: "center" }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: s.total ? (Math.round((s.pass/s.total)*100) >= 85 ? "#6EE7B7" : Math.round((s.pass/s.total)*100) >= 60 ? "#FDE68A" : "#FCA5A5") : C.muted }}>
                        {s.total ? `${Math.round((s.pass/s.total)*100)}%` : "—"}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted }}>Acceptance</div>
                    </div>
                  </div>
                ))}
                {/* Session table — one row per participant, attempts expandable */}
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead><tr style={{ borderBottom: `1px solid ${C.border}`, color: C.muted }}>
                      {["PID","Status","QC Flag","Reason codes","Duration","Words","Engagement","Admin review"].map(h=>(
                        <th key={h} style={{ padding: "6px 10px", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {reconGroups.length === 0
                        ? <tr><td colSpan={8} style={{ padding: "16px 10px", color: C.muted, textAlign: "center" }}>No sessions found.</td></tr>
                        : reconGroups.map(({ key, headline, attempts }) => {
                          const multi = attempts.length > 1;
                          const expanded = !!expandedPids[key];
                          const toggle = () => multi && setExpandedPids(prev => ({ ...prev, [key]: !prev[key] }));
                          return (
                            <React.Fragment key={key}>
                              <tr onClick={toggle} style={{ borderBottom: `1px solid ${C.border}`, cursor: multi ? "pointer" : "default", background: multi && expanded ? "#111827" : "transparent" }}>
                                <td style={{ padding: "6px 10px", fontFamily: "monospace", fontSize: 11 }}>
                                  {multi && (
                                    <span style={{ display: "inline-block", width: 12, color: C.muted, transition: "transform 0.15s", transform: expanded ? "rotate(90deg)" : "none" }}>▸</span>
                                  )}
                                  <span style={{ marginLeft: multi ? 4 : 16 }}>{headline.externalParticipantId || "—"}</span>
                                  {multi && (
                                    <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 99, background: C.border, color: C.muted, fontFamily: "inherit" }}>
                                      · {attempts.length} attempts
                                    </span>
                                  )}
                                </td>
                                {reconCells(headline)}
                              </tr>
                              {multi && expanded && attempts.map(r => (
                                <tr key={r.sessionId} style={{ borderBottom: `1px solid ${C.border}66`, background: "#0B0F19" }}>
                                  <td style={{ padding: "6px 10px 6px 28px", color: C.muted, fontSize: 11 }}>
                                    <span style={{ color: C.border, marginRight: 6 }}>└</span>
                                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ""} · {r.sessionId.slice(0, 8)}
                                  </td>
                                  {reconCells(r)}
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })
                      }
                    </tbody>
                  </table>
                  {reconGroups.length > 0 && (
                    <div style={{ padding: "10px 2px 0", fontSize: 11, color: C.muted }}>
                      Showing {reconGroups.length} participant{reconGroups.length === 1 ? "" : "s"} · {(recon.sessions || []).length} session{(recon.sessions || []).length === 1 ? "" : "s"}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ── Research Corpus Screen (Phase 4) ──────────────────────────────────────────
// Read-only view of the proprietary research corpus: how much standardized data has
// accrued per study type, how many (metric × segment) cells already clear the
// credibility gates ("eligible to surface"), and the current benchmark-surfacing
// governance. Benchmarks stay MUTED until enabled via env config (two-key governance).
function CorpusScreen() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let live = true;
    api("GET", "/corpus/overview")
      .then(d => { if (live) { setData(d); setLoading(false); } })
      .catch(e => { if (live) { setError(e.message); setLoading(false); } });
    return () => { live = false; };
  }, []);

  if (loading) return <div style={{color:C.muted,fontSize:14}}>Loading corpus…</div>;
  if (error)   return <div style={{color:C.danger,fontSize:14}}>Failed to load: {error}</div>;

  const g = data.governance || {};
  const liveBadge = (on) => (
    <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,
      color:on?"#15803D":C.muted,background:on?"rgba(21,128,61,0.12)":C.surface,
      border:`1px solid ${on?"rgba(21,128,61,0.3)":C.border}`}}>{on?"● LIVE":"○ MUTED"}</span>
  );

  return (
    <div>
      {/* Explainer — keeps the feature legible over time */}
      <div style={{padding:"14px 18px",borderRadius:10,background:C.surface,border:`1px solid ${C.border}`,marginBottom:20}}>
        <div style={{fontSize:15,fontWeight:700,color:C.text,marginBottom:6}}>🧠 Research Corpus &amp; Benchmarks</div>
        <div style={{fontSize:12.5,color:C.muted,lineHeight:1.6}}>
          Every closed survey contributes anonymized, standardized metrics (NPS, CSAT, top-box, driver themes)
          into a proprietary corpus. Once a study type accumulates enough data across enough distinct customers,
          its cells become <strong style={{color:C.text}}>eligible</strong> to power industry benchmarks shown in
          customer reports. Surfacing stays <strong style={{color:C.text}}>muted</strong> until turned on via the
          two-key governance below — so the data asset compounds quietly until it is statistically defensible.
        </div>
      </div>

      {/* Governance */}
      <div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:20}}>
        <div style={{flex:"1 1 240px",padding:"14px 16px",borderRadius:10,background:C.surface,border:`1px solid ${C.border}`}}>
          <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Surfacing governance</div>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
            <span style={{fontSize:13,color:C.text}}>Global switch</span> {liveBadge(g.globalEnabled)}
          </div>
          <div style={{fontSize:12,color:C.muted}}>
            Enabled study types: {g.enabledStudyTypes?.length ? g.enabledStudyTypes.join(", ") : <em>none</em>}
          </div>
          <div style={{fontSize:11,color:C.muted,marginTop:8,lineHeight:1.5}}>
            Controlled via <code>CORPUS_NORMS_ENABLED</code> + <code>CORPUS_NORMS_STUDY_TYPES</code>. Flip both to surface.
          </div>
        </div>
        <div style={{flex:"1 1 200px",padding:"14px 16px",borderRadius:10,background:C.surface,border:`1px solid ${C.border}`}}>
          <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Credibility gates</div>
          <div style={{fontSize:12.5,color:C.text,lineHeight:1.8}}>
            Min base: <strong>{data.gates?.minBase}</strong> sessions<br/>
            Min customers: <strong>{data.gates?.minCustomers}</strong><br/>
            Recency: <strong>{data.gates?.recencyMonths}</strong> months
          </div>
        </div>
        <div style={{flex:"1 1 160px",padding:"14px 16px",borderRadius:10,background:C.surface,border:`1px solid ${C.border}`}}>
          <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Corpus size</div>
          <div style={{fontSize:34,fontWeight:800,color:C.accent,lineHeight:1}}>{data.totalEntries}</div>
          <div style={{fontSize:12,color:C.muted,marginTop:4}}>metric snapshots</div>
        </div>
      </div>

      {/* Per-study-type table */}
      {data.studyTypes?.length ? (
        <div style={{borderRadius:10,border:`1px solid ${C.border}`,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{background:C.surface,textAlign:"left",color:C.muted}}>
                {["Study type","Entries","Customers","Surveys","Cells","Eligible","Surfacing"].map((h,i)=>(
                  <th key={i} style={{padding:"10px 14px",fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:0.4}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.studyTypes.map((t,i)=>(
                <tr key={i} style={{borderTop:`1px solid ${C.border}`}}>
                  <td style={{padding:"10px 14px",fontWeight:600,color:C.text}}>{t.studyType}</td>
                  <td style={{padding:"10px 14px",color:C.muted}}>{t.entries}</td>
                  <td style={{padding:"10px 14px",color:t.distinctCustomers>=(data.gates?.minCustomers||3)?C.text:C.muted}}>{t.distinctCustomers}</td>
                  <td style={{padding:"10px 14px",color:C.muted}}>{t.distinctSurveys}</td>
                  <td style={{padding:"10px 14px",color:C.muted}}>{t.distinctCells}</td>
                  <td style={{padding:"10px 14px",fontWeight:700,color:t.eligibleCells>0?"#15803D":C.muted}}>{t.eligibleCells}</td>
                  <td style={{padding:"10px 14px"}}>{liveBadge(t.surfacing)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{padding:"28px",textAlign:"center",color:C.muted,fontSize:13,background:C.surface,borderRadius:10,border:`1px solid ${C.border}`}}>
          No corpus data yet. It accrues automatically as surveys close.
        </div>
      )}
    </div>
  );
}

function Shell({admin,screen,setScreen,onLogout,children}) {
  return (
    <div style={{display:"flex",minHeight:"100dvh",background:C.bg,fontFamily:"'Segoe UI',system-ui,sans-serif",color:C.text}}>
      <style>{RESPONSIVE_STYLES}</style>
      {/* Sidebar (desktop) / Bottom nav (mobile) */}
      <div className="console-sidebar">
        <div className="sidebar-brand">
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <img src="/rik-logo.png" alt="Rik.ai" style={{width:28,height:28,objectFit:"contain"}}/>
            <span style={{fontSize:16,fontWeight:700,color:C.text}}>Rik.ai</span>
            <span style={{background:C.accent,color:"#000",fontSize:9,fontWeight:800,padding:"1px 6px",borderRadius:3,letterSpacing:1.2}}>CONSOLE</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          {NAV.map(item=>{
            const a=screen===item.id;
            return (
              <button key={item.id} onClick={()=>setScreen(item.id)} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"9px 12px",borderRadius:7,border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:a?600:400,background:a?C.accentDim:"transparent",color:a?C.accent:C.muted,marginBottom:2,textAlign:"left",minHeight:44}}>
                <span style={{fontSize:16,flexShrink:0}}>{item.icon}</span>
                <span className="sidebar-label">{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div style={{fontSize:11,color:C.muted,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{admin?.email}</div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <span style={{fontSize:10,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:0.8}}>{admin?.role?.replace("_"," ")}</span>
            <button onClick={onLogout} style={{background:"transparent",border:"none",color:C.muted,fontSize:12,cursor:"pointer",fontFamily:"inherit",padding:"4px 0",minHeight:44,minWidth:44}}>Sign out</button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="console-main">
        <div className="console-topbar">
          <span style={{fontSize:15,fontWeight:600}}>{NAV.find(n=>n.id===screen)?.icon} {NAV.find(n=>n.id===screen)?.label}</span>
          <span style={{marginLeft:"auto",fontSize:12,color:C.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:140}}>{admin?.name}</span>
        </div>
        <div className="console-body">{children}</div>
      </div>
    </div>
  );
}

// ── Root ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [admin,setAdmin]=useState(null);
  const [loading,setLoading]=useState(true);
  const [screen,setScreen]=useState("dashboard");
  const [centerId,setCenterId]=useState(null);
  const [centerSurvey,setCenterSurvey]=useState(null); // { id, status } — auto-open on enter

  function handleLogout() { api("POST","/auth/logout").catch(()=>{}); setAdmin(null); }

  window.__adminLogout = () => setAdmin(null);

  useEffect(()=>{
    api("GET","/auth/me")
      .then(data=>{ if(data?.admin) setAdmin(data.admin); })
      .catch(()=>{})
      .finally(()=>setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  function openCenter(customerId) { setCenterId(customerId); setCenterSurvey(null); }
  function openSurveyInCenter(survey) { setCenterId(survey.customer.id); setCenterSurvey({id:survey.id,status:survey.status}); }

  if (loading) return <><style>{RESPONSIVE_STYLES}</style><div style={{minHeight:"100dvh",background:"#06080F",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{width:28,height:28,borderRadius:"50%",border:"3px solid #1e2533",borderTopColor:"#F59E0B",animation:"spin 0.7s linear infinite"}}/><style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style></div></>;
  if (!admin) return <><style>{RESPONSIVE_STYLES}</style><LoginScreen onLogin={setAdmin}/></>;

  // CustomerCenter renders full-screen outside Shell so it's not clipped by sidebar/padding
  if (centerId) return (
    <>
      <style>{RESPONSIVE_STYLES}</style>
      <CustomerCenter customerId={centerId} onClose={()=>{setCenterId(null);setCenterSurvey(null);}}
        openSurveyId={centerSurvey?.id} openSurveyStatus={centerSurvey?.status}/>
    </>
  );

  return (
    <Shell admin={admin} screen={screen} setScreen={setScreen} onLogout={handleLogout}>
      {screen==="dashboard"    && <DashboardScreen setScreen={setScreen}/>}
      {screen==="participants" && <ParticipantsScreen/>}
      {screen==="customers"    && <CustomersScreen onOpenCenter={openCenter}/>}
      {screen==="surveys"      && <SurveysScreen onOpenSurvey={openSurveyInCenter}/>}
      {screen==="suppliers"    && <SuppliersScreen/>}
      {screen==="payouts"      && <PayoutsScreen/>}
      {screen==="referrals"    && <ReferralsScreen/>}
      {screen==="tickets"      && <TicketsScreen/>}
      {screen==="finance"      && <FinanceScreen/>}
      {screen==="corpus"       && <CorpusScreen/>}
    </Shell>
  );
}
